/* global BigInt */
// src/sections/presale/PresalePage.jsx

import { useEffect, useMemo, useRef, useState } from "react";
import { useConnectModal, useChainModal } from "@rainbow-me/rainbowkit";
import { formatEther, formatUnits, isAddress, parseEther } from "viem";

import Nav from "../../components/Nav";
import Footer from "../../components/Footer";

import useWallet from "../../hooks/useWallet";
import usePresaleV2 from "../../hooks/usePresaleV2";

import "./presale.scss";
import "./dev.scss";

// ✅ NO FALLBACKS: require env for address
const RAW_ENV_PRESALE_ADDRESS = process.env.REACT_APP_PRESALE_ADDRESS;
const RAW_ENV_PRESALE_TEST_ADDRESS = process.env.REACT_APP_PRESALE_TEST_ADDRESS;
const ENV_PRESALE_ADDRESS = (
  RAW_ENV_PRESALE_ADDRESS ||
  RAW_ENV_PRESALE_TEST_ADDRESS ||
  "0x83C7D01693B320d2731ed98d3F643A3234eCEb44"
).trim();
const SHARED_CONTRACT_LS_KEY = "bob_active_presale_contract";

// ✅ Mainnet default
const RAW_ENV_CHAIN_ID = process.env.REACT_APP_PRESALE_CHAIN_ID;
const PRESALE_CHAIN_ID = Number((RAW_ENV_CHAIN_ID || "143").trim());

// Your dev wallet (still useful), but dev tools can also key off on-chain owner when available.
const DEV_ADDRESS = "0xb2cCFE990415595Cd56c4654b473d7809328962a";
const UPCOMING_LAUNCH_TS = Math.floor(Date.UTC(2026, 3, 14, 16, 0, 0) / 1000); // April 14, 2026 12:00 PM ET (EDT on this date)
const LIVE_END_TS = Math.floor(Date.UTC(2026, 3, 16, 16, 0, 0) / 1000); // April 16, 2026 12:00 PM ET (EDT on this date)
const FIXED_ALLOCATION_MON = "1,000";
const FIXED_ALLOCATION_MON_RAW = "1000";
const FIXED_ALLOCATION_BOB = "1,500,000";
const TOTAL_PRESALE_SPOTS = 500;
const PRESALE_SUPPLY_BOB = 750000000;
const TOTAL_SUPPLY_BOB = 1000000000;
const FINAL_ENDED_RAISED_MON = 325000;
const FINAL_ENDED_TARGET_MON = 325000;
const FINAL_ENDED_PARTICIPANTS = 325;
const FINAL_ENDED_MAX_PARTICIPANTS = 325;
const FINAL_ENDED_PRESALE_BOB = 682500000;
const FINAL_ENDED_SUPPLY_LEFT_BOB = 0;
const FINAL_ENDED_BOB_PER_WALLET = 2100000;
const FINAL_ENDED_BOB_PER_MON = 2100;
const DEV_USER_PREVIEW_STATES = ["off", "ineligible", "eligible_not_bought", "eligible_bought"];

function fmt2(nStr) {
  const n = Number(nStr);
  if (!isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatMonShort(mon, opts = {}) {
  const { lessThanOneLabel = null, smallMaxDecimals = 3 } = opts || {};
  const n = Number(mon);

  if (!isFinite(n) || n <= 0) return "0";

  if (n < 1) {
    if (lessThanOneLabel) return lessThanOneLabel;
    const decimals = n < 0.1 ? Math.min(3, smallMaxDecimals) : Math.min(2, smallMaxDecimals);
    return n.toFixed(decimals).replace(/\.?0+$/, "");
  }

  const maxFD = n < 10 ? 2 : n < 100 ? 1 : 1;
  return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: maxFD }).format(n);
}

// ---- allowlist message helpers (UI-only) ----
function isLikelyIneligibleMessage(msg) {
  const m = String(msg || "").toLowerCase();
  if (!m) return false;
  // common "not allowlisted" / 403 patterns
  return (
    m.includes("not eligible") ||
    m.includes("ineligible") ||
    m.includes("not allowlisted") ||
    m.includes("not allow-listed") ||
    m.includes("not on allowlist") ||
    m.includes("not on the allowlist") ||
    m.includes("forbidden") ||
    m.includes("403")
  );
}

function safeInt(v, fallback = 0) {
  const n = Number(v);
  if (!isFinite(n)) return fallback;
  return Math.max(0, Math.floor(n));
}

function downloadTextFile(filename, text, mime = "application/json") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvEscape(v) {
  const s = String(v ?? "");
  if (s.includes('"') || s.includes(",") || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function parseAddressList(raw) {
  return String(raw || "")
    .split(/[\n, ]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .filter((x) => isAddress(x));
}

// --- one-line notice manager helpers ---
function nowMs() {
  return Date.now();
}

function pickTopNotice(candidates) {
  if (!candidates || !candidates.length) return null;
  const sorted = [...candidates].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  return sorted[0];
}

// --- token address helpers ---
function isHexAddress(addr) {
  const s = String(addr || "").trim();
  return /^0x[a-fA-F0-9]{40}$/.test(s);
}

function shortAddr(addr) {
  const s = String(addr || "").trim();
  if (!s) return "";
  if (s.length < 10) return s;
  return `${s.slice(0, 6)}...${s.slice(-4)}`;
}

function formatLiveIn(nowSec, targetSec) {
  const remaining = Math.max(0, Math.floor(Number(targetSec || 0) - Number(nowSec || 0)));
  if (remaining <= 0) return "Live in 0s";
  if (remaining >= 3600) return `Live in ${Math.floor(remaining / 3600)}h`;
  if (remaining >= 60) return `Live in ${Math.floor(remaining / 60)}min`;
  return `Live in ${remaining}s`;
}

function formatEndsIn(nowSec, targetSec) {
  const remaining = Math.max(0, Math.floor(Number(targetSec || 0) - Number(nowSec || 0)));
  if (remaining <= 0) return "Ends in 0s";
  if (remaining >= 3600) return `Ends in ${Math.floor(remaining / 3600)}h`;
  if (remaining >= 60) return `Ends in ${Math.floor(remaining / 60)}min`;
  return `Ends in ${remaining}s`;
}

export default function PresalePage() {
  const wallet = useWallet();
  const { openConnectModal } = useConnectModal();
  const { openChainModal } = useChainModal();
  const [activePresaleAddress, setActivePresaleAddress] = useState(() => {
    try {
      const saved = String(localStorage.getItem(SHARED_CONTRACT_LS_KEY) || "").trim();
      if (saved) return saved;
    } catch {}
    return ENV_PRESALE_ADDRESS;
  });

  const [mounted, setMounted] = useState(false);
  const [countdownNowSec, setCountdownNowSec] = useState(() => Math.floor(Date.now() / 1000));
  const [devMockParticipated, setDevMockParticipated] = useState(false);

  // ----------------------------
  // Dev UI state (persisted)
  // ----------------------------
  const [devBypass, setDevBypass] = useState(false);

  const [capPresaleBps, setCapPresaleBps] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_cap_presale_bps") || "3000"; // 30%
    } catch {
      return "3000";
    }
  });

  const [capWalletBps, setCapWalletBps] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_cap_wallet_bps") || "6"; // 0.06% example
    } catch {
      return "6";
    }
  });

  const [setTpm, setSetTpm] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_tokens_per_mon") || "1000";
    } catch {
      return "1000";
    }
  });

  const [setSoftcapMon, setSetSoftcapMon] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_softcap_mon") || "10";
    } catch {
      return "10";
    }
  });

  const [setStartSec, setSetStartSec] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_start_sec") || "";
    } catch {
      return "";
    }
  });

  const [setEndSec, setSetEndSec] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_end_sec") || "";
    } catch {
      return "";
    }
  });

  // NEW: dev "Start Now" duration input (seconds)
  const [devStartDurationSec, setDevStartDurationSec] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_start_duration_sec") || "120";
    } catch {
      return "120";
    }
  });

  // NEW: "Force Upcoming -> Live" scheduler controls
  const [upcomingHoldSec, setUpcomingHoldSec] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_upcoming_hold_sec") || "15";
    } catch {
      return "15";
    }
  });

  const [scheduledLiveDurationSec, setScheduledLiveDurationSec] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_scheduled_live_duration_sec") || "600";
    } catch {
      return "600";
    }
  });

  // NEW: refund cursor maxSteps input for cursor loops
  const [refundMaxSteps, setRefundMaxSteps] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_refund_max_steps") || "120";
    } catch {
      return "120";
    }
  });

  // NEW: dev drawer open/close
  const [devOpen, setDevOpen] = useState(() => {
    try {
      return localStorage.getItem("bob_devconsole_open") === "1";
    } catch {
      return false;
    }
  });

  // NEW: dev "preview user mode" (hides dev-only info inside presale UI)
  const [devPreviewUserMode, setDevPreviewUserMode] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_preview_user_mode") === "1";
    } catch {
      return false;
    }
  });

  const [devUserPreviewState, setDevUserPreviewState] = useState(() => {
    try {
      const saved = localStorage.getItem("bob_dev_user_preview_state") || "off";
      return DEV_USER_PREVIEW_STATES.includes(saved) ? saved : "off";
    } catch {
      return "off";
    }
  });

  // NEW: dev-only "enable logs"
  const [devLogsOn, setDevLogsOn] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_logs_on") === "1";
    } catch {
      return false;
    }
  });

  // NEW: dev-only "log once"
  const [devLogOnce, setDevLogOnce] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_log_once") !== "0"; // default ON
    } catch {
      return true;
    }
  });

  // NEW: Apply-all busy state + inline status
  const [applyAllBusy, setApplyAllBusy] = useState(false);
  const [applyAllStatus, setApplyAllStatus] = useState("");

  // DEV-ONLY: local override fields kept for reference + pasting
  const [authDeadlineLocal, setAuthDeadlineLocal] = useState(() => {
    try {
      return localStorage.getItem("bob_presale_auth_deadline") || "";
    } catch {
      return "";
    }
  });

  const [authSigLocal, setAuthSigLocal] = useState(() => {
    try {
      return localStorage.getItem("bob_presale_auth_sig") || "";
    } catch {
      return "";
    }
  });

  // NEW: export buyers (dev-only)
  const [exportBusy, setExportBusy] = useState(false);

  // NEW v2.8.9: force refund buyer input (escape hatch)
  const [forceRefundBuyer, setForceRefundBuyer] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_force_refund_buyer") || "";
    } catch {
      return "";
    }
  });

  // NEW: dev token address input (persisted) + "set token address" dev action
  const [setTokenAddr, setSetTokenAddr] = useState(() => {
    try {
      return localStorage.getItem("bob_dev_token_address") || "";
    } catch {
      return "";
    }
  });

  // ✅ NEW: claims enabled toggle (local state synced from hook)
  const [claimsEnabledLocal, setClaimsEnabledLocal] = useState(false);

  // V2 owner console inputs (mirrors presaletest)
  const [v2ContributionMon, setV2ContributionMon] = useState("1000");
  const [v2MaxParticipants, setV2MaxParticipants] = useState("500");
  const [v2ResetContributionMon, setV2ResetContributionMon] = useState("1000");
  const [v2ResetMaxParticipants, setV2ResetMaxParticipants] = useState("500");
  const [v2ResetState, setV2ResetState] = useState("0");
  const [v2AllowlistInput, setV2AllowlistInput] = useState("");

  // Persist dev inputs
  useEffect(() => {
    try {
      localStorage.setItem("bob_devconsole_open", devOpen ? "1" : "0");
    } catch {}
  }, [devOpen]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_preview_user_mode", devPreviewUserMode ? "1" : "0");
    } catch {}
  }, [devPreviewUserMode]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_user_preview_state", devUserPreviewState);
    } catch {}
  }, [devUserPreviewState]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_logs_on", devLogsOn ? "1" : "0");
    } catch {}
  }, [devLogsOn]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_log_once", devLogOnce ? "1" : "0");
    } catch {}
  }, [devLogOnce]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_presale_auth_deadline", authDeadlineLocal || "");
    } catch {}
  }, [authDeadlineLocal]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_presale_auth_sig", authSigLocal || "");
    } catch {}
  }, [authSigLocal]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_cap_presale_bps", String(capPresaleBps || ""));
    } catch {}
  }, [capPresaleBps]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_cap_wallet_bps", String(capWalletBps || ""));
    } catch {}
  }, [capWalletBps]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_tokens_per_mon", String(setTpm || ""));
    } catch {}
  }, [setTpm]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_softcap_mon", String(setSoftcapMon || ""));
    } catch {}
  }, [setSoftcapMon]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_start_sec", String(setStartSec || ""));
    } catch {}
  }, [setStartSec]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_end_sec", String(setEndSec || ""));
    } catch {}
  }, [setEndSec]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_start_duration_sec", String(devStartDurationSec || ""));
    } catch {}
  }, [devStartDurationSec]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_upcoming_hold_sec", String(upcomingHoldSec || ""));
    } catch {}
  }, [upcomingHoldSec]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_scheduled_live_duration_sec", String(scheduledLiveDurationSec || ""));
    } catch {}
  }, [scheduledLiveDurationSec]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_refund_max_steps", String(refundMaxSteps || ""));
    } catch {}
  }, [refundMaxSteps]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_force_refund_buyer", String(forceRefundBuyer || ""));
    } catch {}
  }, [forceRefundBuyer]);

  useEffect(() => {
    try {
      localStorage.setItem("bob_dev_token_address", String(setTokenAddr || ""));
    } catch {}
  }, [setTokenAddr]);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setCountdownNowSec(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  // ✅ Call hook with explicit address/chain (no fallbacks in this file)
  const presale = usePresaleV2(activePresaleAddress, wallet.address, PRESALE_CHAIN_ID);

  useEffect(() => {
    try {
      const saved = String(localStorage.getItem(SHARED_CONTRACT_LS_KEY) || "").trim();
      const next = saved || ENV_PRESALE_ADDRESS;
      if (next && next !== activePresaleAddress) setActivePresaleAddress(next);
    } catch {}
  }, [activePresaleAddress]);

  // ----------------------------
  // ✅ UI-safe status fields (prevents rare undefined-access flickers)
  // ----------------------------
  const participantCountSafe = useMemo(() => {
    const n = Number(presale?.participantCount ?? 0);
    return isFinite(n) && n >= 0 ? n : 0;
  }, [presale?.participantCount]);

  const maxParticipantsSafe = useMemo(() => {
    const n = Number(presale?.maxParticipants ?? TOTAL_PRESALE_SPOTS);
    return isFinite(n) && n > 0 ? n : TOTAL_PRESALE_SPOTS;
  }, [presale?.maxParticipants]);

  const contributionMonPerWallet = useMemo(() => {
    try {
      const n = Number(formatEther(presale?.contributionWeiPerWallet ?? 0n));
      return isFinite(n) && n > 0 ? n : Number(FIXED_ALLOCATION_MON_RAW);
    } catch {
      return Number(FIXED_ALLOCATION_MON_RAW);
    }
  }, [presale?.contributionWeiPerWallet]);

  const contributionMonRaw = useMemo(() => {
    if (!isFinite(contributionMonPerWallet) || contributionMonPerWallet <= 0) return FIXED_ALLOCATION_MON_RAW;
    const rounded = Math.round(contributionMonPerWallet);
    const isInt = Math.abs(contributionMonPerWallet - rounded) < 1e-9;
    return isInt ? String(rounded) : String(contributionMonPerWallet);
  }, [contributionMonPerWallet]);

  const allocationMonDisplay = useMemo(() => {
    if (!isFinite(contributionMonPerWallet) || contributionMonPerWallet <= 0) return FIXED_ALLOCATION_MON;
    const rounded = Math.round(contributionMonPerWallet);
    const isInt = Math.abs(contributionMonPerWallet - rounded) < 1e-9;
    return contributionMonPerWallet.toLocaleString(undefined, { maximumFractionDigits: isInt ? 0 : 2 });
  }, [contributionMonPerWallet]);

  const bobPerWalletValue = useMemo(() => {
    const max = Math.max(1, Number(maxParticipantsSafe || 1));
    const n = PRESALE_SUPPLY_BOB / max;
    if (!isFinite(n) || n <= 0) return Number(FIXED_ALLOCATION_BOB.replace(/,/g, ""));
    return Math.floor(n);
  }, [maxParticipantsSafe]);

  const bobPerMonValue = useMemo(() => {
    const mon = Number(contributionMonPerWallet || 0);
    if (!isFinite(mon) || mon <= 0) return 0;
    const n = bobPerWalletValue / mon;
    return isFinite(n) && n > 0 ? n : 0;
  }, [bobPerWalletValue, contributionMonPerWallet]);

  const filledSpots = useMemo(() => Math.min(participantCountSafe, maxParticipantsSafe), [participantCountSafe, maxParticipantsSafe]);
  const statusKey = useMemo(() => {
    const fromHook = String(presale?.status?.key || "").toLowerCase();
    if (filledSpots >= maxParticipantsSafe) return "ended";
    if (fromHook === "upcoming" || fromHook === "ended" || fromHook === "live") return fromHook;
    return "live";
  }, [presale?.status?.key, filledSpots, maxParticipantsSafe]);
  const statusLabel = statusKey === "upcoming" ? "Upcoming" : statusKey === "ended" ? "Ended" : "Live";
  const statusBoxLabel = useMemo(() => {
    if (statusKey === "upcoming") return formatLiveIn(countdownNowSec, UPCOMING_LAUNCH_TS);
    if (statusKey === "live") return formatEndsIn(countdownNowSec, LIVE_END_TS);
    return statusLabel;
  }, [statusKey, statusLabel, countdownNowSec]);

  const raisedMonValue = useMemo(() => {
    try {
      const n = Number(formatEther(presale?.totalRaisedWei ?? 0n));
      return isFinite(n) && n >= 0 ? n : 0;
    } catch {
      return 0;
    }
  }, [presale?.totalRaisedWei]);

  const soldSupplyBob = useMemo(() => Math.min(PRESALE_SUPPLY_BOB, Math.max(0, filledSpots * bobPerWalletValue)), [filledSpots, bobPerWalletValue]);
  const presaleAllocationBob = useMemo(
    () => Math.min(TOTAL_SUPPLY_BOB, Math.max(0, soldSupplyBob)),
    [soldSupplyBob]
  );
  const raisedMonUi = statusKey === "ended" ? FINAL_ENDED_RAISED_MON : raisedMonValue;
  const participantsUi = statusKey === "ended" ? FINAL_ENDED_PARTICIPANTS : filledSpots;
  const maxParticipantsUi = statusKey === "ended" ? FINAL_ENDED_MAX_PARTICIPANTS : maxParticipantsSafe;
  const participantsStatusCapUi = statusKey === "ended" ? FINAL_ENDED_PARTICIPANTS : maxParticipantsUi;
  const bobPerWalletUi = statusKey === "ended" ? FINAL_ENDED_BOB_PER_WALLET : bobPerWalletValue;
  const bobPerWalletDisplayUi = bobPerWalletUi.toLocaleString();
  const bobPerMonValueUi = statusKey === "ended" ? FINAL_ENDED_BOB_PER_MON : bobPerMonValue;
  const bobPerMonDisplayUi = useMemo(() => {
    const n = Number(bobPerMonValueUi || 0);
    if (!isFinite(n) || n <= 0) return "0";
    return new Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: n < 10000 ? 1 : 2,
    }).format(n);
  }, [bobPerMonValueUi]);
  const presaleAllocationBobUi = statusKey === "ended" ? FINAL_ENDED_PRESALE_BOB : presaleAllocationBob;
  const liquidityAllocationBobUi = useMemo(() => Math.max(0, TOTAL_SUPPLY_BOB - presaleAllocationBobUi), [presaleAllocationBobUi]);
  const presaleAllocationPctUi = useMemo(
    () => Math.round((presaleAllocationBobUi / TOTAL_SUPPLY_BOB) * 100),
    [presaleAllocationBobUi]
  );
  const liquidityAllocationPctUi = useMemo(() => Math.max(0, 100 - presaleAllocationPctUi), [presaleAllocationPctUi]);
  const remainingSupplyBobUi = useMemo(() => Math.max(0, TOTAL_SUPPLY_BOB - presaleAllocationBobUi), [presaleAllocationBobUi]);
  const raisedMonShortUi = useMemo(() => formatMonShort(raisedMonUi, { lessThanOneLabel: null, smallMaxDecimals: 0 }), [raisedMonUi]);
  const targetMonTotalUi = useMemo(
    () => (statusKey === "ended" ? FINAL_ENDED_TARGET_MON : Math.max(1, Math.round(maxParticipantsUi * contributionMonPerWallet))),
    [statusKey, maxParticipantsUi, contributionMonPerWallet]
  );
  const progressPctUi = useMemo(() => {
    const n = (raisedMonUi / targetMonTotalUi) * 100;
    return isFinite(n) ? n : 0;
  }, [raisedMonUi, targetMonTotalUi]);
  const progressSupplyLeftBobUi = statusKey === "ended" ? FINAL_ENDED_SUPPLY_LEFT_BOB : remainingSupplyBobUi;
  const progressSupplyCapBobUi = statusKey === "ended" ? FINAL_ENDED_PRESALE_BOB : TOTAL_SUPPLY_BOB;

  // Prefer on-chain owner when hook exposes it; fallback to DEV_ADDRESS for dev gating only
  const ownerFromHook = presale.owner || presale.ownerAddress || presale.contractOwner || presale.ownerAddr || "";

  const walletLower = wallet.address ? String(wallet.address).toLowerCase() : "";
  const devLower = String(DEV_ADDRESS).toLowerCase();
  const ownerLower = ownerFromHook ? String(ownerFromHook).toLowerCase() : "";

  const isDev = wallet.isConnected && wallet.address && (walletLower === ownerLower || walletLower === devLower);

  // Keep the page clean: dev tools only render for dev address / owner
  const canShowOwnerTools = Boolean(isDev);
  const devPreviewPersonaActive = canShowOwnerTools && devPreviewUserMode && devUserPreviewState !== "off";
  const previewIsIneligible = devPreviewPersonaActive && devUserPreviewState === "ineligible";
  const previewIsEligibleNotBought = devPreviewPersonaActive && devUserPreviewState === "eligible_not_bought";
  const previewIsEligibleBought = devPreviewPersonaActive && devUserPreviewState === "eligible_bought";

  // Determine if user is on wrong network (best-effort)
  const expectedChainId = Number(presale.chainId || presale.expectedChainId || PRESALE_CHAIN_ID);
  const walletChainId = Number(wallet.chainId || wallet.networkId || 0);
  const isWrongNetwork = wallet.isConnected && walletChainId && walletChainId !== expectedChainId;

  // Notice formatting
  const showDevInfoInUi = canShowOwnerTools && !devPreviewUserMode;

  // ✅ Sync local claims enabled state from hook
  useEffect(() => {
    setClaimsEnabledLocal(Boolean(presale.claimsEnabled));
  }, [presale.claimsEnabled]);

  // ----------------------------
  // ✅ Notifications (success/fail buy/refund/claim, etc.)
  // NOTE: kept, but now surfaced via a single stable one-line bar (no layout shift).
  // ----------------------------
  const [uiNotifs, setUiNotifs] = useState([]); // {id,type,title,body,mono,createdAt}
  const notifTimersRef = useRef({}); // id -> timeout
  const notifIdRef = useRef(1);

  const clearNotifTimer = (id) => {
    try {
      if (notifTimersRef.current[id]) clearTimeout(notifTimersRef.current[id]);
    } catch {}
    try {
      delete notifTimersRef.current[id];
    } catch {}
  };

  const removeNotif = (id) => {
    clearNotifTimer(id);
    setUiNotifs((prev) => prev.filter((n) => n.id !== id));
  };

  // Minimal: keep only ONE active notification (prevents spam) + enforce readable durations via NoticeBar.
  const pushNotif = (n, ttlMs = 4500) => {
    const id = `n${Date.now()}_${notifIdRef.current++}`;
    const item = {
      id,
      type: n.type || "info",
      title: n.title || "",
      body: n.body || "",
      mono: Boolean(n.mono),
      createdAt: Date.now(),
    };

    setUiNotifs(() => [item]); // ✅ ONLY ONE

    if (ttlMs && ttlMs > 0) {
      clearNotifTimer(id);
      notifTimersRef.current[id] = setTimeout(() => removeNotif(id), ttlMs);
    }

    return id;
  };

  // Cleanup notif timers on unmount
  useEffect(() => {
    return () => {
      try {
        Object.keys(notifTimersRef.current || {}).forEach((k) => clearNotifTimer(k));
      } catch {}
      notifTimersRef.current = {};
    };
  }, []);

  // Track last user action to label tx outcomes
  const lastUserActionRef = useRef({
    kind: "", // "buy" | "refund" | "claim" | "dev" | ""
    startedAt: 0,
    submittedHash: "",
    sawConfirming: false,
    done: false,
  });

  const markActionStart = (kind) => {
    lastUserActionRef.current = {
      kind: String(kind || ""),
      startedAt: Date.now(),
      submittedHash: "",
      sawConfirming: false,
      done: false,
    };
  };

  const markActionDone = () => {
    lastUserActionRef.current.done = true;
  };

  const lastSeenTxHashRef = useRef("");
  const prevConfirmingRef = useRef(false);
  const prevUiErrorRef = useRef("");

  // When a tx hash appears, show "submitted" notice (only if dev-view; for users it's just noise)
  useEffect(() => {
    const h = String(presale.txHash || "");
    if (!h) return;

    if (lastSeenTxHashRef.current === h) return;
    lastSeenTxHashRef.current = h;

    if (!lastUserActionRef.current.done && !lastUserActionRef.current.submittedHash) {
      lastUserActionRef.current.submittedHash = h;
    }

    if (!showDevInfoInUi) return; // ✅ minimal for users

    const kind = lastUserActionRef.current.kind || "tx";
    const title =
      kind === "buy"
        ? "Buy submitted"
        : kind === "refund"
        ? "Refund submitted"
        : kind === "claim"
        ? "Claim submitted"
        : "Transaction submitted";
    const body = `Tx: ${h}`;
    pushNotif({ type: "info", title, body, mono: true }, 4500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presale.txHash, showDevInfoInUi]);

  // Watch confirming transitions to decide success
  useEffect(() => {
    const confirming = Boolean(presale.isConfirming);
    const prev = Boolean(prevConfirmingRef.current);
    prevConfirmingRef.current = confirming;

    if (confirming && !lastUserActionRef.current.done) {
      lastUserActionRef.current.sawConfirming = true;
    }

    if (prev && !confirming) {
      if (lastUserActionRef.current.done) return;

      const kind = lastUserActionRef.current.kind || "";
      const currentErr = String(presale.uiError || "");
      const hadError = Boolean(currentErr);

      if (!hadError) {
        if (kind === "buy") {
          pushNotif({ type: "success", title: "Buy confirmed", body: "Your presale buy was confirmed.", mono: false }, 5000);
        } else if (kind === "refund") {
          pushNotif({ type: "success", title: "Refund confirmed", body: "Your refund was confirmed.", mono: false }, 5000);
        } else if (kind === "claim") {
          pushNotif({ type: "success", title: "Claim confirmed", body: "Your claim was confirmed.", mono: false }, 5000);
        } else {
          pushNotif({ type: "success", title: "Transaction confirmed", body: "Your transaction was confirmed.", mono: false }, 5000);
        }

        try {
          presale.forceRefresh?.();
        } catch {}
      }

      markActionDone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presale.isConfirming]);

  // If uiError changes while an action is pending, show a failure notification
  useEffect(() => {
    const err = String(presale.uiError || "");
    const prev = String(prevUiErrorRef.current || "");
    prevUiErrorRef.current = err;

    if (!err) return;
    if (err === prev) return;
    if (lastUserActionRef.current.done) return;

    const kind = lastUserActionRef.current.kind || "";
    const title =
      kind === "buy" ? "Buy failed" : kind === "refund" ? "Refund failed" : kind === "claim" ? "Claim failed" : kind ? "Transaction failed" : "Error";

    const body = showDevInfoInUi ? err : "Transaction failed. Please try again (or reconnect your wallet).";
    pushNotif({ type: "error", title, body, mono: showDevInfoInUi }, 6500);

    markActionDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presale.uiError, showDevInfoInUi]);

  // Close drawer on Escape
  useEffect(() => {
    if (!canShowOwnerTools) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setDevOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canShowOwnerTools]);

  // keep checkbox synced to on-chain read
  useEffect(() => {
    setDevBypass(Boolean(presale.devAllowlistBypass));
  }, [presale.devAllowlistBypass]);

  // ----------------------------
  // ✅ Helper: prompt chain switch for dev actions too
  // ----------------------------
  const ensureCorrectChain = () => {
    if (!activePresaleAddress) return false;
    if (!presale.presaleReady) return false;

    if (!wallet.isConnected) {
      if (openConnectModal) openConnectModal();
      return false;
    }

    if (isWrongNetwork) {
      if (openChainModal) openChainModal();
      return false;
    }

    return true;
  };

  // ✅ IMPORTANT: never let dev tx fail silently
  const runDevTx = async (label, fn) => {
    if (!ensureCorrectChain()) {
      if (canShowOwnerTools && devLogsOn) {
        // eslint-disable-next-line no-console
        console.log(`[DevTx:${label}] blocked`, {
          hasAddress: Boolean(activePresaleAddress),
          presaleReady: Boolean(presale.presaleReady),
          walletConnected: Boolean(wallet.isConnected),
          walletChainId,
          expectedChainId,
          wrongNetwork: Boolean(isWrongNetwork),
        });
      }
      return false;
    }

    try {
      if (canShowOwnerTools && devLogsOn) {
        // eslint-disable-next-line no-console
        console.log(`[DevTx:${label}] starting...`);
      }

      const res = fn?.();
      if (res && typeof res.then === "function") await res;

      try {
        presale.forceRefresh?.();
      } catch {}

      if (canShowOwnerTools && devLogsOn) {
        // eslint-disable-next-line no-console
        console.log(`[DevTx:${label}] done`);
      }
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[DevTx:${label}]`, e);

      const msg = e?.shortMessage || e?.cause?.shortMessage || e?.details || e?.message || String(e);
      alert(`[DevTx:${label}] ${msg}`);
      return false;
    }
  };

  const v2OwnerSetContribution = () =>
    runDevTx("v2:setContributionWeiPerWallet", () =>
      presale.setContributionWeiPerWallet(parseEther(String(v2ContributionMon || "0")))
    );

  const v2OwnerSetMaxParticipants = () =>
    runDevTx("v2:setMaxParticipants", () => presale.setMaxParticipants(BigInt(String(v2MaxParticipants || "0"))));

  const v2OwnerResetPresale = () =>
    runDevTx("v2:resetPresale", () =>
      presale.resetPresale(
        parseEther(String(v2ResetContributionMon || "0")),
        BigInt(String(v2ResetMaxParticipants || "0")),
        Number(v2ResetState || 0)
      )
    );

  const v2OwnerAddAllowlist = () =>
    runDevTx("v2:addToAllowlist", () => presale.addToAllowlist(parseAddressList(v2AllowlistInput)));

  const v2OwnerRemoveAllowlist = () =>
    runDevTx("v2:removeFromAllowlist", () => presale.removeFromAllowlist(parseAddressList(v2AllowlistInput)));

  // ----------------------------
  // ✅ Dev: set token address (supports multiple hook method names)
  // ----------------------------
  const tokenAddrFromHook =
    presale.claimToken ||
    presale.tokenAddress ||
    presale.bobToken ||
    presale.token ||
    presale.tokenAddr ||
    presale.tokenContract ||
    presale.bobTokenAddress ||
    "";

  const setTokenAddressOnChain = async (addr) => {
    const a = String(addr || "").trim();
    if (!a) {
      alert("Token address is empty.");
      return false;
    }
    if (!isHexAddress(a)) {
      alert("Invalid address. Expected 0x + 40 hex chars.");
      return false;
    }

    return runDevTx("setTokenAddress", async () => {
      // Try common hook method names (whichever exists)
      if (typeof presale.setClaimToken === "function") return presale.setClaimToken(a);
      if (typeof presale.devSetTokenAddress === "function") return presale.devSetTokenAddress(a);
      if (typeof presale.devSetToken === "function") return presale.devSetToken(a);
      if (typeof presale.setTokenAddress === "function") return presale.setTokenAddress(a);
      if (typeof presale.setTokenAddr === "function") return presale.setTokenAddr(a);
      if (typeof presale.setToken === "function") return presale.setToken(a);

      throw new Error("Hook missing token setter. Add a method in usePresaleEscrow (e.g., setClaimToken).");
    });
  };

  // ----------------------------
  // ✅ Export buyers -> file (dev-only)
  // ----------------------------
  const exportBuyersToFile = async (format = "json") => {
    if (exportBusy) return;
    if (!activePresaleAddress || !presale.presaleReady) {
      alert("Presale not ready / missing address.");
      return;
    }

    setExportBusy(true);

    try {
      let addrs = [];
      let weiContrib = [];
      let tokBought = [];

      // Try single-call first
      try {
        const res = await presale.exportAllBuyers?.();
        if (Array.isArray(res) && res.length === 3) {
          addrs = res[0] || [];
          weiContrib = res[1] || [];
          tokBought = res[2] || [];
        } else if (res && res.addrs) {
          addrs = res.addrs || [];
          weiContrib = res.weiContrib || [];
          tokBought = res.tokBought || [];
        } else {
          throw new Error("Unexpected exportAllBuyers() result shape.");
        }
      } catch {
        // Fallback: paged
        const pageSize = 200;
        let start = 0;

        while (true) {
          const r = await presale.exportBuyersPaged?.(start, pageSize);
          let a = [];
          let w = [];
          let t = [];

          if (Array.isArray(r) && r.length === 3) {
            a = r[0] || [];
            w = r[1] || [];
            t = r[2] || [];
          } else if (r && r.addrs) {
            a = r.addrs || [];
            w = r.weiContrib || [];
            t = r.tokBought || [];
          } else {
            throw new Error("exportBuyersPaged() missing / wrong shape.");
          }

          if (!a.length) break;

          addrs = addrs.concat(a);
          weiContrib = weiContrib.concat(w);
          tokBought = tokBought.concat(t);
          start += pageSize;
        }
      }

      const rows = (addrs || []).map((addr, i) => {
        const w = weiContrib?.[i] ?? 0n;
        const t = tokBought?.[i] ?? 0n;
        return {
          address: String(addr),
          contributedWei: String(w),
          contributedMon: String(formatUnits(w, 18)),
          tokensBoughtWei: String(t),
          tokensBought: String(formatUnits(t, 18)),
        };
      });

      const ts = new Date().toISOString().replace(/[:.]/g, "-");

      if (format === "csv") {
        const header = ["address", "contributedWei", "contributedMon", "tokensBoughtWei", "tokensBought"];
        const lines = [header.join(",")].concat(
          rows.map((r) =>
            [csvEscape(r.address), csvEscape(r.contributedWei), csvEscape(r.contributedMon), csvEscape(r.tokensBoughtWei), csvEscape(r.tokensBought)].join(",")
          )
        );
        downloadTextFile(`bob-presale-buyers-${ts}.csv`, lines.join("\n"), "text/csv");
      } else {
        downloadTextFile(`bob-presale-buyers-${ts}.json`, JSON.stringify(rows, null, 2), "application/json");
      }
    } catch (e) {
      const msg = e?.shortMessage || e?.message || String(e);
      alert(`Export failed: ${msg}`);
    } finally {
      setExportBusy(false);
    }
  };

  // ----------------------------
  // ✅ "Force Upcoming -> Live" scheduler (frontend convenience)
  // ----------------------------
  const autoLiveRef = useRef({
    active: false,
    liveAt: 0,
    timerId: null,
  });

  const [autoLiveActive, setAutoLiveActive] = useState(false);
  const [autoLiveCountdown, setAutoLiveCountdown] = useState("");

  const clearAutoLive = () => {
    try {
      if (autoLiveRef.current.timerId) clearTimeout(autoLiveRef.current.timerId);
    } catch {}
    autoLiveRef.current.active = false;
    autoLiveRef.current.liveAt = 0;
    autoLiveRef.current.timerId = null;
    setAutoLiveActive(false);
    setAutoLiveCountdown("");
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => clearAutoLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown updater
  useEffect(() => {
    if (!autoLiveActive) return;

    const id = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const liveAt = safeInt(autoLiveRef.current.liveAt, 0);
      const left = Math.max(0, liveAt - now);

      if (!liveAt || left <= 0) {
        setAutoLiveCountdown("now");
        return;
      }

      if (left < 60) {
        setAutoLiveCountdown(`${left}s`);
        return;
      }
      const m = Math.floor(left / 60);
      const s = left % 60;
      setAutoLiveCountdown(`${m}m ${s}s`);
    }, 250);

    return () => clearInterval(id);
  }, [autoLiveActive]);

  const scheduleUpcomingThenLive = async () => {
    const hold = safeInt(upcomingHoldSec, 0);
    const dur = safeInt(scheduledLiveDurationSec, 0);

    if (hold <= 0) {
      alert("Upcoming hold must be > 0 seconds.");
      return;
    }
    if (dur <= 0) {
      alert("Live duration must be > 0 seconds.");
      return;
    }

    const ok1 = await runDevTx("ForceUpcoming", () => presale.devSetPhase(0));
    if (!ok1) return;

    const now = Math.floor(Date.now() / 1000);
    const start = now + hold;
    const end = start + dur;

    setSetStartSec(String(start));
    setSetEndSec(String(end));

    const ok2 = await runDevTx("SetTimes(forScheduled)", () => presale.setTimes(start, end));
    if (!ok2) return;

    clearAutoLive();

    autoLiveRef.current.active = true;
    autoLiveRef.current.liveAt = start;
    setAutoLiveActive(true);

    autoLiveRef.current.timerId = setTimeout(async () => {
      const ok = await runDevTx("AutoForceLive", () => presale.devSetPhase(1));
      if (ok) {
        setTimeout(() => {
          try {
            presale.forceRefresh?.();
          } catch {}
        }, 1200);
      }
      clearAutoLive();
    }, hold * 1000);
  };

  // Cancel auto-live when you manually change phase (avoids surprise)
  const wrapPhaseAction = (label, fn) => {
    return runDevTx(label, async () => {
      clearAutoLive();
      return fn?.();
    });
  };

  // ----------------------------
  // ✅ Apply-all convenience
  // ----------------------------
  const applyAll = async () => {
    if (applyAllBusy) return;

    setApplyAllBusy(true);
    setApplyAllStatus("Starting...");

    const ok = await runDevTx("ApplyAll", async () => {
      setApplyAllStatus("1/7: Apply bypass...");
      await presale.devSetAllowlistBypass(Boolean(devBypass));

      setApplyAllStatus("2/7: Apply caps...");
      await presale.setCapBps(capPresaleBps, capWalletBps);

      setApplyAllStatus("3/7: Set rate (tokensPerMon)...");
      await presale.setTokensPerMon(setTpm);

      setApplyAllStatus("4/7: Set softcap...");
      await presale.setSoftCapMon(setSoftcapMon);

      // Token address (optional; only if valid)
      const tok = String(setTokenAddr || "").trim();
      if (tok && isHexAddress(tok)) {
        setApplyAllStatus("5/7: Set token address...");
        // use the same compatibility logic as the dedicated button
        if (typeof presale.setClaimToken === "function") await presale.setClaimToken(tok);
        else if (typeof presale.devSetTokenAddress === "function") await presale.devSetTokenAddress(tok);
        else if (typeof presale.devSetToken === "function") await presale.devSetToken(tok);
        else if (typeof presale.setTokenAddress === "function") await presale.setTokenAddress(tok);
        else if (typeof presale.setTokenAddr === "function") await presale.setTokenAddr(tok);
        else if (typeof presale.setToken === "function") await presale.setToken(tok);
        else throw new Error("Hook missing token setter for ApplyAll.");
      } else {
        setApplyAllStatus("5/7: Token address skipped (blank/invalid).");
      }

      // Claims enabled (optional)
      setApplyAllStatus("6/7: Set claims enabled...");
      if (typeof presale.setClaimsEnabled === "function") {
        await presale.setClaimsEnabled(Boolean(claimsEnabledLocal));
      } else {
        setApplyAllStatus("6/7: Claims toggle skipped (hook missing setClaimsEnabled).");
      }

      const s = safeInt(setStartSec, 0);
      const e = safeInt(setEndSec, 0);
      if (s && e && e > s) {
        setApplyAllStatus("7/7: Set times...");
        await presale.setTimes(s, e);
      } else {
        setApplyAllStatus("7/7: Times skipped (invalid/missing).");
      }
    });

    if (ok) {
      setApplyAllStatus("Done.");
      setTimeout(() => setApplyAllStatus(""), 1200);
    } else {
      setApplyAllStatus("");
    }

    setApplyAllBusy(false);
  };

  const loadCurrentIntoInputs = () => {
    try {
      if (typeof presale.walletCapBps === "number") setCapWalletBps(String(presale.walletCapBps));
      if (typeof presale.presaleAllocationBps === "number") setCapPresaleBps(String(presale.presaleAllocationBps));

      if (typeof presale.tokensPerMon === "bigint") {
        const tpm = formatUnits(presale.tokensPerMon, 18);
        setSetTpm(String(tpm).replace(/,/g, ""));
      }

      // Load current token address from hook if exposed
      const onchainTok =
        presale.claimToken ||
        presale.tokenAddress ||
        presale.bobToken ||
        presale.token ||
        presale.tokenAddr ||
        presale.tokenContract ||
        presale.bobTokenAddress ||
        "";
      if (typeof onchainTok === "string" && onchainTok.trim()) setSetTokenAddr(onchainTok.trim());

      // Load claims enabled state
      setClaimsEnabledLocal(Boolean(presale.claimsEnabled));
    } catch {}
  };

  // ----------------------------
  // Dev-only captured console
  // ----------------------------
  const [devConsoleLines, setDevConsoleLines] = useState([]);
  const devConsoleLinesRef = useRef(devConsoleLines);
  devConsoleLinesRef.current = devConsoleLines;

  const pushDevLine = (level, args) => {
    const line = {
      t: Date.now(),
      level,
      msg: args
        .map((a) => {
          try {
            if (typeof a === "string") return a;
            return JSON.stringify(a, null, 0);
          } catch {
            return String(a);
          }
        })
        .join(" "),
    };

    const next = [...devConsoleLinesRef.current, line].slice(-240);
    devConsoleLinesRef.current = next;
    setDevConsoleLines(next);
  };

  useEffect(() => {
    if (!canShowOwnerTools) return;
    if (!devLogsOn) return;

    const origLog = console.log;
    const origWarn = console.warn;
    const origErr = console.error;

    console.log = (...args) => {
      try {
        pushDevLine("log", args);
      } catch {}
      origLog(...args);
    };
    console.warn = (...args) => {
      try {
        pushDevLine("warn", args);
      } catch {}
      origWarn(...args);
    };
    console.error = (...args) => {
      try {
        pushDevLine("error", args);
      } catch {}
      origErr(...args);
    };

    return () => {
      console.log = origLog;
      console.warn = origWarn;
      console.error = origErr;
    };
  }, [canShowOwnerTools, devLogsOn]);

  const devLoggedOnceRef = useRef(false);
  const devLog = (...args) => {
    if (!canShowOwnerTools) return;
    if (!devLogsOn) return;
    // eslint-disable-next-line no-console
    console.log(...args);
  };

  // DEV-ONLY ENV/RUNTIME LOGS
  useEffect(() => {
    if (!canShowOwnerTools) return;
    if (!devLogsOn) return;
    if (devLogOnce && devLoggedOnceRef.current) return;
    devLoggedOnceRef.current = true;

    devLog("[PresalePage ENV]", {
      RAW_ENV_PRESALE_ADDRESS,
      activePresaleAddress,
      RAW_ENV_CHAIN_ID,
      PRESALE_CHAIN_ID,
    });

    devLog("[PresalePage Runtime]", {
      presaleAddress: activePresaleAddress,
      chainIdConfig: PRESALE_CHAIN_ID,
      walletConnected: Boolean(wallet.isConnected),
      walletAddress: wallet.address || "",
      walletChainId: wallet.chainId || wallet.networkId || 0,
    });
  }, [canShowOwnerTools, devLogsOn, devLogOnce, wallet.isConnected, wallet.address, wallet.chainId, wallet.networkId]);

  useEffect(() => {
    if (!canShowOwnerTools) return;
    if (!devLogsOn) return;
    if (devLogOnce && devLoggedOnceRef.current) return;

    devLog("[PresalePage Hook Snapshot]", {
      presaleReady: Boolean(presale.presaleReady),
      statusKey: presale?.status?.key,
      normalizedStatusKey: statusKey,
      statusLabel: presale?.status?.label,
      expectedChainId: presale.chainId || presale.expectedChainId || PRESALE_CHAIN_ID,
      signer: presale.signer || "",
      owner: presale.owner || presale.ownerAddress || presale.contractOwner || "",
      manualPhaseSet: Boolean(presale.manualPhaseSet),
      manualPhaseValue: presale.manualPhaseValue,
      devMode: Boolean(presale.devMode),
      devAllowlistBypass: Boolean(presale.devAllowlistBypass),
      refundCursor: presale.refundCursor ? String(presale.refundCursor) : "0",
      claimToken: presale.claimToken || "",
      claimsEnabled: Boolean(presale.claimsEnabled),
      tokenAddress:
        presale.tokenAddress ||
        presale.bobToken ||
        presale.token ||
        presale.tokenAddr ||
        presale.tokenContract ||
        presale.bobTokenAddress ||
        "",
      uiError: presale.uiError || "",
      buyDisabledReason: presale.buyDisabledReason || "",
      txHash: presale.txHash || "",
      authState: presale.authStatus?.state || "",
      authMsg: presale.authStatus?.msg || "",
      authDeadline: presale.authDeadline || 0,
      authSigLen: presale.authSig ? String(presale.authSig).length : 0,
      outstandingWei: presale.outstandingContributionsWei ? String(presale.outstandingContributionsWei) : "0",
      forceToTreasuryWei: presale.totalForceToTreasuryWei ? String(presale.totalForceToTreasuryWei) : "0",
    });
  }, [
    canShowOwnerTools,
    devLogsOn,
    devLogOnce,
    statusKey,
    presale.presaleReady,
    presale.status?.key,
    presale.status?.label,
    presale.chainId,
    presale.expectedChainId,
    presale.signer,
    presale.owner,
    presale.ownerAddress,
    presale.contractOwner,
    presale.manualPhaseSet,
    presale.manualPhaseValue,
    presale.devMode,
    presale.devAllowlistBypass,
    presale.refundCursor,
    presale.claimToken,
    presale.claimsEnabled,
    presale.tokenAddress,
    presale.bobToken,
    presale.token,
    presale.tokenAddr,
    presale.tokenContract,
    presale.bobTokenAddress,
    presale.uiError,
    presale.buyDisabledReason,
    presale.txHash,
    presale.authStatus?.state,
    presale.authStatus?.msg,
    presale.authDeadline,
    presale.authSig,
    presale.outstandingContributionsWei,
    presale.totalForceToTreasuryWei,
  ]);

  // Expose a quick debug helper in DevTools (dev-only)
  useEffect(() => {
    if (!canShowOwnerTools) return;

    window.__presaleDebug = () => {
      try {
        const snap = presale && presale.debugSnapshot ? presale.debugSnapshot() : { error: "debugSnapshot missing" };
        // eslint-disable-next-line no-console
        console.log("[Presale Debug]", snap);
        return snap;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log("[Presale Debug] failed", e);
        return null;
      }
    };

    return () => {
      try {
        delete window.__presaleDebug;
      } catch {}
    };
  }, [canShowOwnerTools, presale]);

  const heroSubtitle = useMemo(() => {
    if (statusKey === "live") return "Presale is live.";
    if (statusKey === "ended") return "Presale filled. Eligible wallets will receive tokens via airdrop.";
    return "Presale is upcoming.";
  }, [statusKey]);

  const rightTagline = useMemo(() => {
    if (statusKey === "live") return "";
    if (statusKey === "ended") return "Presale is complete. Eligible wallets will receive an airdrop.";
    return "";
  }, [statusKey]);

  const buyBtnText = useMemo(() => {
    if (statusKey === "live") {
      if (!wallet.isConnected) return "Connect wallet";
      if (isWrongNetwork) return "Switch network";
      return presale.isWriting || presale.isConfirming ? "Processing..." : "Buy in presale";
    }
    if (statusKey === "ended") return "Presale ended";
    return "Presale upcoming";
  }, [statusKey, presale.isWriting, presale.isConfirming, wallet.isConnected, isWrongNetwork]);

  const userContribWei = useMemo(() => {
    const candidates = [
      presale.userContributionWei,
      presale.userContributedWei,
      presale.userContribWei,
      presale.contributedWei,
      presale.userWeiContributed,
    ].filter((v) => v !== undefined && v !== null);

    for (const v of candidates) {
      try {
        if (typeof v === "bigint") return v;
        if (typeof v === "number" && isFinite(v)) return BigInt(Math.floor(v));
        if (typeof v === "string" && v.trim()) return BigInt(v.trim());
      } catch {}
    }
    return 0n;
  }, [
    presale.userContributionWei,
    presale.userContributedWei,
    presale.userContribWei,
    presale.contributedWei,
    presale.userWeiContributed,
  ]);

  const shortAddress = wallet.address ? wallet.address.slice(0, 6) + "..." + wallet.address.slice(-4) : "";
  const fixedAmountValue = presale.amountMon;
  const setFixedAmountValue = presale.setAmountMon;
  const isPresaleReady = presale.presaleReady;

  useEffect(() => {
    setDevMockParticipated(false);
  }, [walletLower]);

  useEffect(() => {
    if (!isPresaleReady) return;
    if (fixedAmountValue === contributionMonRaw) return;
    if (typeof setFixedAmountValue === "function") {
      setFixedAmountValue(contributionMonRaw);
    }
  }, [isPresaleReady, fixedAmountValue, setFixedAmountValue, contributionMonRaw]);

  // Primary CTA behavior
  const handlePrimaryCta = async () => {
    if (!activePresaleAddress) return;
    if (!presale.presaleReady) return;

    // ✅ IMPORTANT: never try to buy unless LIVE (prevents errors when button text says "Refunds available")
    if (statusKey !== "live") return;

    if (canShowOwnerTools && (shouldDisableUi || (devPreviewUserMode && !previewIsIneligible))) {
      setDevMockParticipated(true);
      pushNotif(
        {
          type: "success",
          title: "Preview enabled",
          body: "Showing the participated state for UI preview.",
          mono: false,
        },
        3000
      );
      return;
    }

    if (!wallet.isConnected) {
      if (openConnectModal) openConnectModal();
      return;
    }

    if (isWrongNetwork) {
      if (openChainModal) openChainModal();
      return;
    }

    if (canShowOwnerTools && devLogsOn) {
      // eslint-disable-next-line no-console
      console.log("[BUY CLICK] auth snapshot", {
        authStatus: presale.authStatus,
        authDeadline: presale.authDeadline,
        authSig: presale.authSig,
        authSigLen: presale.authSig ? String(presale.authSig).length : 0,
        signer: presale.signer || "",
        userNonce: presale.userNonce || "",
        devMode: Boolean(presale.devMode),
        bypass: Boolean(presale.devAllowlistBypass),
        presaleReady: Boolean(presale.presaleReady),
        status: presale.status?.key,
        expectedChainId,
        walletChainId,
        amountMon: presale.amountMon,
      });

      // eslint-disable-next-line no-console
      console.log("[BUY CLICK] full debugSnapshot (if available)");
      try {
        if (typeof presale.debugSnapshot === "function") {
          // eslint-disable-next-line no-console
          console.log(presale.debugSnapshot());
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log("[BUY CLICK] debugSnapshot failed", e);
      }
    }

    try {
      markActionStart("buy");
      // ✅ Minimal + actionable (one line will show for >= min time)
      pushNotif({ type: "info", title: "Confirm in wallet", body: "Confirm the transaction in your wallet.", mono: false }, 4500);
      await presale.buy();
    } catch (e) {
      const msg = e?.shortMessage || e?.cause?.shortMessage || e?.details || e?.message || String(e);
      pushNotif(
        {
          type: "error",
          title: "Buy failed",
          body: showDevInfoInUi ? msg : "Buy failed or was rejected. Please try again.",
          mono: showDevInfoInUi,
        },
        6500
      );
      markActionDone();
    }
  };

  const uiErrorSafe = useMemo(() => {
    if (!presale.uiError) return "";
    if (showDevInfoInUi) return String(presale.uiError);
    return "Something went wrong. Please try again. If it keeps happening, reconnect your wallet.";
  }, [presale.uiError, showDevInfoInUi]);

  const buyDisabled = presale.isWriting || presale.isConfirming;

  // ----------------------------
  // ✅ Allowlist UI stabilizer
  // ----------------------------
  const authKickRef = useRef("");

  const authState = presale.authStatus?.state || "";
  const authMsg = presale.authStatus?.msg || "";

  const hasAuth = Boolean(authState === "ok" || (presale.authDeadline && presale.authSig));

  const needsPrivateAllowlist =
    statusKey === "live" &&
    wallet.isConnected &&
    Boolean(walletLower) &&
    !isWrongNetwork &&
    !canShowOwnerTools &&
    !(presale.devMode && presale.devAllowlistBypass);

  const needsPrivateAllowlistUi =
    previewIsIneligible ||
    (!previewIsEligibleNotBought && !previewIsEligibleBought && needsPrivateAllowlist);

  useEffect(() => {
    authKickRef.current = "";
  }, [walletLower]);

  useEffect(() => {
    if (!needsPrivateAllowlist) return;
    if (!wallet.isConnected) return;
    if (hasAuth) return;
    if (authState === "fetching" || authState === "loading") return;
    if (!presale?.fetchAuth || typeof presale.fetchAuth !== "function") return;

    const key = (walletLower || "") + "|" + String(statusKey || "");
    if (!key) return;
    if (authKickRef.current === key) return;
    authKickRef.current = key;
    presale.fetchAuth("page-ensure-auth");
  }, [needsPrivateAllowlist, wallet.isConnected, hasAuth, authState, presale.fetchAuth, walletLower, statusKey]);

  const allowlistUi = useMemo(() => {
    if (previewIsIneligible) {
      return {
        kind: "ineligible",
        type: "warning",
        title: "Not eligible",
        body: "This wallet is not eligible for the presale.",
        mono: false,
      };
    }

    if (previewIsEligibleNotBought || previewIsEligibleBought) {
      return { kind: "none", title: "", body: "", type: "info", mono: false };
    }

    if (!needsPrivateAllowlistUi) {
      return { kind: "none", title: "", body: "", type: "info", mono: false };
    }

    if (hasAuth || authState === "ok") {
      return { kind: "none", title: "", body: "", type: "info", mono: false };
    }

    if (authState === "denied" || isLikelyIneligibleMessage(authMsg)) {
      return {
        kind: "ineligible",
        type: "warning",
        title: "Not eligible",
        body: "This wallet is not eligible for the presale.",
        mono: false,
      };
    }

    if (authState === "error") {
      return {
        kind: "error",
        type: "warning",
        title: "Eligibility check failed",
        body: showDevInfoInUi ? authMsg || "Could not fetch authorization. Try again." : "Could not verify eligibility. Try reconnecting.",
        mono: showDevInfoInUi ? true : false,
      };
    }

    if (authState === "loading" || authState === "fetching" || authState === "idle") {
      return {
        kind: "checking",
        type: "info",
        title: "Checking eligibility",
        body: "Checking eligibility...",
        mono: false,
      };
    }

    return {
      kind: "error",
      type: "warning",
      title: "Eligibility check failed",
      body: "Could not verify eligibility. Try reconnecting.",
      mono: false,
    };
  }, [
    previewIsIneligible,
    previewIsEligibleNotBought,
    previewIsEligibleBought,
    needsPrivateAllowlistUi,
    authState,
    authMsg,
    hasAuth,
    showDevInfoInUi,
  ]);

  const isIneligible = useMemo(() => {
    return needsPrivateAllowlistUi && allowlistUi.kind === "ineligible";
  }, [needsPrivateAllowlistUi, allowlistUi.kind]);

  // Participation state (used for CTA)
  const hasParticipated = userContribWei > 0n;
  const hasParticipatedUi = hasParticipated || devMockParticipated || previewIsEligibleBought;

  const effectiveBuyBtnText = useMemo(() => {
    if (statusKey !== "live") return buyBtnText;
    if (!wallet.isConnected) return buyBtnText;
    if (isWrongNetwork) return buyBtnText;
    if (hasParticipatedUi) return "Participated";
    if (needsPrivateAllowlistUi) {
      if (allowlistUi.kind === "ineligible") return "Not eligible";
      if (allowlistUi.kind === "error") return "Eligibility check failed";
      if (allowlistUi.kind === "checking") return "Checking eligibility...";
    }
    return buyBtnText;
  }, [statusKey, buyBtnText, wallet.isConnected, isWrongNetwork, hasParticipatedUi, needsPrivateAllowlistUi, allowlistUi.kind]);

  const hasVerifiedAllowlistAuth = useMemo(() => {
    if (!needsPrivateAllowlistUi) return true;
    return Boolean(hasAuth);
  }, [needsPrivateAllowlistUi, hasAuth]);

  const shouldDisableUi = useMemo(() => {
    if (!activePresaleAddress) return true;
    if (!presale.presaleReady) return true;
    if (statusKey !== "live") return true;
    if (!wallet.isConnected) return true;
    if (isWrongNetwork) return true;
    if (!hasVerifiedAllowlistAuth) return true;
    if (isIneligible) return true;
    return false;
  }, [activePresaleAddress, presale.presaleReady, statusKey, wallet.isConnected, isWrongNetwork, hasVerifiedAllowlistAuth, isIneligible]);

  const authHelperText = useMemo(() => {
    if (!canShowOwnerTools) return "";
    if (!activePresaleAddress) return "Missing presale address.";
    if (!presale.presaleReady) return "Presale not configured.";
    if (presale.devMode && presale.devAllowlistBypass) return "Bypass is ON (dev). Signature not required for buys.";
    return "Bypass is OFF. Auth should be auto-fetched from your backend when live.";
  }, [canShowOwnerTools, presale.presaleReady, presale.devMode, presale.devAllowlistBypass]);

  const canEditAuth = canShowOwnerTools;

  const ownerBtnDisabled = presale.isWriting || presale.isConfirming || applyAllBusy || exportBusy;

  // ✅ IMPORTANT CHANGE:
  // - When not LIVE, we want the CTA to be STATIC (not clickable), not a disabled button.
  // - So we DON'T rely on disabled for non-live states; we will render a <div> instead.
  const buyButtonDisabled =
    buyDisabled ||
    !activePresaleAddress ||
    (!canShowOwnerTools && shouldDisableUi) ||
    (statusKey === "live" && hasParticipatedUi);

  const refundCursorDisplay = useMemo(() => {
    try {
      if (!presale.presaleReady) return "0";
      return String(presale.refundCursor ?? 0n);
    } catch {
      return "0";
    }
  }, [presale.presaleReady, presale.refundCursor]);

  // NEW v2.8.9: dev-only accounting displays
  const outstandingMonDisplay = useMemo(() => {
    try {
      const v = presale.outstandingContributionsWei ?? 0n;
      return fmt2(formatUnits(v, 18));
    } catch {
      return "0";
    }
  }, [presale.outstandingContributionsWei]);

  const forcedToTreasuryMonDisplay = useMemo(() => {
    try {
      const v = presale.totalForceToTreasuryWei ?? 0n;
      return fmt2(formatUnits(v, 18));
    } catch {
      return "0";
    }
  }, [presale.totalForceToTreasuryWei]);

  // ----------------------------
  // ✅ Single-line Notice Bar (stable height + minimal + readable)
  // ----------------------------
  const NOTICE_MIN_SHOW_MS = 2600; // keep messages readable
  const NOTICE_DEBOUNCE_MS = 450; // don't flash "checking"
  const [notice, setNotice] = useState({ id: "idle", text: "", level: "info" });
  const noticeRef = useRef({ shownAt: 0, lastId: "idle", pendingTimer: null });

  const noticeCandidates = useMemo(() => {
    const c = [];

    // Top: recent user-facing notif (ONE only)
    const topNotif = uiNotifs && uiNotifs.length ? uiNotifs[0] : null;
    if (topNotif) {
      const level = topNotif.type === "warning" ? "warning" : topNotif.type === "success" ? "success" : topNotif.type === "error" ? "error" : "info";
      const pr = topNotif.type === "error" ? 220 : topNotif.type === "success" ? 200 : topNotif.type === "warning" ? 190 : 140;
      const text = topNotif.title ? `${topNotif.title}${topNotif.body ? ": " + topNotif.body : ""}` : topNotif.body || "";
      if (text) {
        c.push({ id: `notif_${topNotif.id}`, text, level, priority: pr });
      }
    }

    if (statusKey === "live") {
      if (!wallet.isConnected) {
        c.push({ id: "connect", text: "Connect your wallet to participate.", level: "info", priority: 160 });
      } else if (isWrongNetwork) {
        c.push({ id: "wrong_network", text: `Wrong network. Switch to chain ${String(expectedChainId)}.`, level: "warning", priority: 200 });
      } else if (needsPrivateAllowlistUi) {
        if (allowlistUi.kind === "ineligible") {
          c.push({ id: "ineligible", text: "This wallet is not eligible for the presale.", level: "warning", priority: 195 });
        } else if (allowlistUi.kind === "error") {
          c.push({ id: "auth_error", text: "Could not verify eligibility. Try reconnecting.", level: "warning", priority: 185 });
        } else if (allowlistUi.kind === "checking") {
          c.push({ id: "checking", text: "Checking eligibility…", level: "info", priority: 40 });
        }
      }
    }

    if (statusKey === "ended") {
      c.push({ id: "airdrop_delivery", text: "Presale is complete. Eligible wallets will receive tokens via airdrop.", level: "info", priority: 155 });
    }

    if (uiErrorSafe) {
      c.push({ id: "ui_error", text: showDevInfoInUi ? uiErrorSafe : "Something went wrong. Please try again.", level: "error", priority: 215 });
    }

    return c;
  }, [
    uiNotifs,
    statusKey,
    wallet.isConnected,
    isWrongNetwork,
    expectedChainId,
    needsPrivateAllowlistUi,
    allowlistUi.kind,
    uiErrorSafe,
    showDevInfoInUi,
  ]);

  useEffect(() => {
    const desired = pickTopNotice(noticeCandidates) || { id: "idle", text: "", level: "info", priority: 0 };

    const canReplaceNow = () => {
      const elapsed = nowMs() - (noticeRef.current.shownAt || 0);
      return elapsed >= NOTICE_MIN_SHOW_MS || noticeRef.current.lastId === "idle";
    };

    const applyNotice = (n) => {
      noticeRef.current.lastId = n.id;
      noticeRef.current.shownAt = nowMs();
      setNotice({ id: n.id, text: n.text, level: n.level });
    };

    if (noticeRef.current.pendingTimer) {
      clearTimeout(noticeRef.current.pendingTimer);
      noticeRef.current.pendingTimer = null;
    }

    if (desired.id === noticeRef.current.lastId) return;

    const currentCandidate = noticeCandidates.find((x) => x.id === noticeRef.current.lastId);
    const currentPrio = currentCandidate?.priority ?? 0;
    const desiredPrio = desired.priority ?? 0;

    if (!canReplaceNow() && desiredPrio <= currentPrio) return;

    if (desired.id === "checking") {
      noticeRef.current.pendingTimer = setTimeout(() => {
        const top2 = pickTopNotice(noticeCandidates);
        if (top2 && top2.id === "checking" && top2.id !== noticeRef.current.lastId) {
          applyNotice(top2);
        }
      }, NOTICE_DEBOUNCE_MS);
      return;
    }

    if (desired.id === "idle") {
      if (canReplaceNow()) applyNotice(desired);
      else noticeRef.current.pendingTimer = setTimeout(() => applyNotice(desired), NOTICE_MIN_SHOW_MS);
      return;
    }

    applyNotice(desired);
  }, [noticeCandidates]);

  const authStateLine = useMemo(() => {
    if (!notice.text) return "";
    return notice.text;
  }, [notice.text]);

  const devUserPreviewLabel = useMemo(() => {
    if (devUserPreviewState === "ineligible") return "Preview: Ineligible";
    if (devUserPreviewState === "eligible_not_bought") return "Preview: Eligible (Not bought)";
    if (devUserPreviewState === "eligible_bought") return "Preview: Eligible (Bought)";
    return "Preview: Off";
  }, [devUserPreviewState]);

  return (
    <>
      <Nav useSmoothScroll={false} />

      {/* Dev drawer toggle + drawer (only for dev wallet / on-chain owner) */}
      {canShowOwnerTools ? (
        <>
          <button
            type="button"
            className={"bob-devtoggle " + (devOpen ? "bob-devtoggle--open" : "")}
            onClick={() => setDevOpen((v) => !v)}
            aria-label={devOpen ? "Close dev console" : "Open dev console"}
          >
            {devOpen ? "Close Dev" : "Dev"}
          </button>

          <div className={"bob-devdrawer " + (devOpen ? "bob-devdrawer--open" : "")} role="dialog" aria-label="Dev Console">
            <div className="bob-devdrawer__top">
              <div className="bob-devdrawer__title">
                <div className="bob-devdrawer__h">Dev Console</div>
                <div className="bob-devdrawer__sub">Only visible to owner/dev wallet. Press Esc to close.</div>
              </div>

              <div className="bob-devdrawer__actions">
                <button
                  type="button"
                  className={"max-btn bob-devpreview " + (devPreviewUserMode ? "bob-devpreview--on" : "")}
                  onClick={() => setDevPreviewUserMode((v) => !v)}
                  title="Hide dev info inside the presale UI to preview the normal user view"
                >
                  {devPreviewUserMode ? "User View: ON" : "User View: OFF"}
                </button>

                <button
                  type="button"
                  className={"max-btn bob-devlogs " + (devLogsOn ? "bob-devlogs--on" : "")}
                  onClick={() => setDevLogsOn((v) => !v)}
                  title="Capture console logs + enable dev logging"
                >
                  {devLogsOn ? "Logs: ON" : "Logs: OFF"}
                </button>

                <button
                  type="button"
                  className={"max-btn bob-devlogonce " + (devLogOnce ? "bob-devlogonce--on" : "")}
                  onClick={() => {
                    devLoggedOnceRef.current = false;
                    setDevLogOnce((v) => !v);
                  }}
                  title="When ON, logs happen once per page load (recommended)."
                >
                  {devLogOnce ? "Log Once: ON" : "Log Once: OFF"}
                </button>

                <button type="button" className="max-btn" onClick={() => runDevTx("forceRefresh", () => presale.forceRefresh())} disabled={ownerBtnDisabled}>
                  Refresh
                </button>

                <button type="button" className="max-btn" onClick={() => setDevOpen(false)} disabled={ownerBtnDisabled}>
                  Close
                </button>
              </div>
            </div>

            <div className="bob-devdrawer__body">
              <div className="bob-devconsole bob-devconsole--drawer" style={{ marginBottom: "12px" }}>
                <div className="bob-devconsole__group bob-devconsole__group--wide">
                  <div className="bob-devconsole__label">V2 Owner Console (PresaleTest Controls)</div>
                  <div className="bob-devconsole__hint">
                    Contract: <span className="bob-devconsole__v--mono">{activePresaleAddress || "(missing)"}</span>
                    <br />
                    Owner: <span className="bob-devconsole__v--mono">{ownerFromHook || "(unknown)"}</span>
                    <br />
                    Round: <span className="bob-devconsole__v--mono">{String(presale.roundId || 0)}</span> | State:{" "}
                    <span className="bob-devconsole__v--mono">{String(presale.presaleState || 0)}</span> | Contribution:{" "}
                    <span className="bob-devconsole__v--mono">{String(presale.contributionWeiPerWallet || 0n)} wei</span> | Max:{" "}
                    <span className="bob-devconsole__v--mono">{String(presale.maxParticipants || 0n)}</span>
                  </div>

                  <div className="bob-devconsole__split" style={{ marginTop: "10px" }}>
                    <div className="bob-devconsole__field">
                      <div className="bob-devconsole__k">Set state</div>
                      <div className="bob-devconsole__btnrow">
                        <button type="button" className="max-btn" onClick={() => runDevTx("v2:setPresaleState:0", () => presale.devSetPhase(0))} disabled={ownerBtnDisabled}>
                          UPCOMING
                        </button>
                        <button type="button" className="max-btn" onClick={() => runDevTx("v2:setPresaleState:1", () => presale.devSetPhase(1))} disabled={ownerBtnDisabled}>
                          LIVE
                        </button>
                        <button type="button" className="max-btn" onClick={() => runDevTx("v2:setPresaleState:2", () => presale.devSetPhase(2))} disabled={ownerBtnDisabled}>
                          ENDED
                        </button>
                      </div>
                    </div>

                    <div className="bob-devconsole__field">
                      <div className="bob-devconsole__k">Set contribution (MON)</div>
                      <input value={v2ContributionMon} onChange={(e) => setV2ContributionMon(e.target.value)} placeholder="1000" />
                      <button type="button" className="max-btn" onClick={v2OwnerSetContribution} disabled={ownerBtnDisabled}>
                        Apply contribution
                      </button>
                    </div>
                  </div>

                  <div className="bob-devconsole__split">
                    <div className="bob-devconsole__field">
                      <div className="bob-devconsole__k">Set max participants</div>
                      <input value={v2MaxParticipants} onChange={(e) => setV2MaxParticipants(e.target.value)} placeholder="500" />
                      <button type="button" className="max-btn" onClick={v2OwnerSetMaxParticipants} disabled={ownerBtnDisabled}>
                        Apply max participants
                      </button>
                    </div>

                    <div className="bob-devconsole__field">
                      <div className="bob-devconsole__k">Reset presale</div>
                      <input
                        value={v2ResetContributionMon}
                        onChange={(e) => setV2ResetContributionMon(e.target.value)}
                        placeholder="new contribution MON"
                      />
                      <input
                        value={v2ResetMaxParticipants}
                        onChange={(e) => setV2ResetMaxParticipants(e.target.value)}
                        placeholder="new max participants"
                      />
                      <select value={v2ResetState} onChange={(e) => setV2ResetState(e.target.value)}>
                        <option value="0">UPCOMING</option>
                        <option value="1">LIVE</option>
                        <option value="2">ENDED</option>
                      </select>
                      <button type="button" className="max-btn" onClick={v2OwnerResetPresale} disabled={ownerBtnDisabled}>
                        Reset presale
                      </button>
                    </div>
                  </div>

                  <div className="bob-devconsole__field">
                    <div className="bob-devconsole__k">Allowlist management (comma/newline separated addresses)</div>
                    <textarea
                      rows={4}
                      value={v2AllowlistInput}
                      onChange={(e) => setV2AllowlistInput(e.target.value)}
                      placeholder="0x..., 0x..."
                    />
                    <div className="bob-devconsole__btnrow">
                      <button type="button" className="max-btn" onClick={v2OwnerAddAllowlist} disabled={ownerBtnDisabled}>
                        Add allowlist entries
                      </button>
                      <button type="button" className="max-btn" onClick={v2OwnerRemoveAllowlist} disabled={ownerBtnDisabled}>
                        Remove allowlist entries
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* DEV-ONLY debug strip */}
              <div className="bob-devstrip" aria-label="Presale Debug Strip">
                <div className="bob-devstrip__row">
                  <span className="k">env addr</span>
                  <span className="v">{activePresaleAddress ? `${activePresaleAddress.slice(0, 6)}...${activePresaleAddress.slice(-4)}` : "(missing)"}</span>
                </div>
                <div className="bob-devstrip__row">
                  <span className="k">page chainId</span>
                  <span className="v">
                    {String(PRESALE_CHAIN_ID)} <span className="muted">(env raw: {RAW_ENV_CHAIN_ID || "(unset)"})</span>
                  </span>
                </div>
                <div className="bob-devstrip__row">
                  <span className="k">expected/wallet</span>
                  <span className={"v " + (isWrongNetwork ? "bad" : "")}>
                    {String(expectedChainId)} / {String(walletChainId)} {isWrongNetwork ? " | WRONG NETWORK" : ""}
                  </span>
                </div>
                <div className="bob-devstrip__row">
                  <span className="k">ready/status</span>
                  <span className="v">
                    {presale.presaleReady ? "true" : "false"} / {presale?.status?.key || "(none)"}{" "}
                    <span className="muted">
                      | normalized={statusKey} | manual={presale.manualPhaseSet ? "true" : "false"}:{String(presale.manualPhaseValue ?? "")}
                    </span>
                  </span>
                </div>
                <div className="bob-devstrip__row">
                  <span className="k">owner/signer</span>
                  <span className="v">
                    {ownerFromHook ? `${String(ownerFromHook).slice(0, 6)}...${String(ownerFromHook).slice(-4)}` : "(not exposed)"} <span className="muted">|</span>{" "}
                    {presale.signer ? `${String(presale.signer).slice(0, 6)}...${String(presale.signer).slice(-4)}` : "(none)"}
                  </span>
                </div>

                {/* Token address display */}
                <div className="bob-devstrip__row">
                  <span className="k">token</span>
                  <span className="v">
                    {tokenAddrFromHook ? shortAddr(tokenAddrFromHook) : "(unset)"}{" "}
                    {showDevInfoInUi && tokenAddrFromHook ? <span className="muted">| {String(tokenAddrFromHook)}</span> : null}
                  </span>
                </div>

                {/* ✅ NEW: claims enabled display */}
                <div className="bob-devstrip__row">
                  <span className="k">claims</span>
                  <span className="v">
                    {presale.claimsEnabled ? "ENABLED" : "DISABLED"}
                  </span>
                </div>

                <div className="bob-devstrip__row">
                  <span className="k">refund cursor</span>
                  <span className="v">
                    {refundCursorDisplay} <span className="muted">/ buyers {String(presale.participantCount || 0)}</span>
                  </span>
                </div>

                {/* NEW v2.8.9 */}
                <div className="bob-devstrip__row">
                  <span className="k">outstanding</span>
                  <span className="v">
                    {outstandingMonDisplay} <span className="muted">$MON</span>
                  </span>
                </div>
                <div className="bob-devstrip__row">
                  <span className="k">forced→treasury</span>
                  <span className="v">
                    {forcedToTreasuryMonDisplay} <span className="muted">$MON</span>
                  </span>
                </div>

                <div className="bob-devstrip__row">
                  <span className="k">auth</span>
                  <span className="v">
                    state={presale.authStatus?.state || "(none)"} | deadline={String(presale.authDeadline || 0)} | sigLen={presale.authSig ? String(presale.authSig).length : 0}
                  </span>
                </div>
              </div>

              {/* Dev console */}
              <div className="bob-devconsole bob-devconsole--drawer">
                <div className="bob-devconsole__grid">
                  <div className="bob-devconsole__group">
                    <div className="bob-devconsole__label">Phase</div>
                    <div className="bob-devconsole__btns">
                      <button type="button" className="max-btn" onClick={() => wrapPhaseAction("devSetPhase:0", () => presale.devSetPhase(0))} disabled={ownerBtnDisabled}>
                        Force Upcoming
                      </button>
                      <button type="button" className="max-btn" onClick={() => wrapPhaseAction("devSetPhase:1", () => presale.devSetPhase(1))} disabled={ownerBtnDisabled}>
                        Force Live
                      </button>
                      <button type="button" className="max-btn" onClick={() => wrapPhaseAction("devSetPhase:2", () => presale.devSetPhase(2))} disabled={ownerBtnDisabled}>
                        Force Ended (Success)
                      </button>
                      <button type="button" className="max-btn" onClick={() => wrapPhaseAction("devSetPhase:3", () => presale.devSetPhase(3))} disabled={ownerBtnDisabled}>
                        Force Ended (Fail)
                      </button>
                      <button type="button" className="max-btn" onClick={() => wrapPhaseAction("devSetPhase:4", () => presale.devSetPhase(4))} disabled={ownerBtnDisabled}>
                        Force Canceled
                      </button>
                      <button type="button" className="max-btn" onClick={() => wrapPhaseAction("devClearPhase", () => presale.devClearPhase())} disabled={ownerBtnDisabled}>
                        Clear Manual Phase
                      </button>
                    </div>

                    <div className="bob-devconsole__hint" style={{ marginTop: "8px" }}>
                      manualPhaseSet={presale.manualPhaseSet ? "true" : "false"} | manualPhaseValue={String(presale.manualPhaseValue ?? "")}
                      {autoLiveActive ? (
                        <>
                          <br />
                          <span className="muted">Auto LIVE in: </span>
                          <span className="bob-devconsole__v--mono">{autoLiveCountdown || "..."}</span>
                        </>
                      ) : null}
                    </div>
                  </div>

                  <div className="bob-devconsole__group">
                    <div className="bob-devconsole__label">Time</div>

                    <div className="bob-devconsole__split">
                      <div className="bob-devconsole__field">
                        <div className="bob-devconsole__k">Start Now duration (sec)</div>
                        <input value={devStartDurationSec} onChange={(e) => setDevStartDurationSec(e.target.value)} placeholder="120" />
                        <div className="bob-devconsole__mini">Calls devStartNow(duration)</div>
                      </div>

                      <div className="bob-devconsole__field">
                        <div className="bob-devconsole__k">End</div>
                        <div className="bob-devconsole__mini">Calls devEndNow()</div>
                      </div>
                    </div>

                    <div className="bob-devconsole__btnrow">
                      <button type="button" className="max-btn" onClick={() => runDevTx("devStartNow", () => presale.devStartNow(safeInt(devStartDurationSec, 120)))} disabled={ownerBtnDisabled}>
                        Start Now
                      </button>
                      <button type="button" className="max-btn" onClick={() => runDevTx("devEndNow", () => presale.devEndNow())} disabled={ownerBtnDisabled}>
                        End Now
                      </button>
                    </div>

                    <div className="bob-devconsole__hint" style={{ marginTop: "10px" }}>
                      <strong>Upcoming → Live scheduler</strong> (frontend convenience)
                      <br />
                      Forces Upcoming immediately, sets start/end times for user-facing countdown, then forces Live after the hold.
                    </div>

                    <div className="bob-devconsole__split">
                      <div className="bob-devconsole__field">
                        <div className="bob-devconsole__k">Hold Upcoming (sec)</div>
                        <input value={upcomingHoldSec} onChange={(e) => setUpcomingHoldSec(e.target.value)} placeholder="15" />
                      </div>
                      <div className="bob-devconsole__field">
                        <div className="bob-devconsole__k">Live duration (sec)</div>
                        <input value={scheduledLiveDurationSec} onChange={(e) => setScheduledLiveDurationSec(e.target.value)} placeholder="600" />
                      </div>
                    </div>

                    <div className="bob-devconsole__btnrow">
                      <button type="button" className="max-btn" onClick={scheduleUpcomingThenLive} disabled={ownerBtnDisabled || autoLiveActive}>
                        Force Upcoming → Live
                      </button>
                      <button type="button" className="max-btn" onClick={clearAutoLive} disabled={ownerBtnDisabled || !autoLiveActive}>
                        Cancel auto-live
                      </button>
                    </div>

                    {autoLiveActive ? (
                      <div className="bob-devconsole__mini" style={{ marginTop: "6px" }}>
                        Auto forcing LIVE in <span className="bob-devconsole__v--mono">{autoLiveCountdown || "..."}</span> (keep this tab open)
                      </div>
                    ) : null}
                  </div>

                  {/* Refund tools */}
                  <div className="bob-devconsole__group">
                    <div className="bob-devconsole__label">Refunds</div>

                    <div className="bob-devconsole__hint" style={{ marginBottom: "8px" }}>
                      Cursor refund is safest for "refund-all": it won't brick if one wallet fails or balance is low.
                      <br />
                      refundCursor=<span className="bob-devconsole__v--mono">{refundCursorDisplay}</span>
                      <br />
                      outstanding=<span className="bob-devconsole__v--mono">{outstandingMonDisplay}</span> <span className="muted">$MON</span>
                    </div>

                    <div className="bob-devconsole__split">
                      <div className="bob-devconsole__field">
                        <div className="bob-devconsole__k">maxSteps (1..500)</div>
                        <input value={refundMaxSteps} onChange={(e) => setRefundMaxSteps(e.target.value)} placeholder="120" />
                        <div className="bob-devconsole__mini">How many buyers to process per call.</div>
                      </div>
                      <div className="bob-devconsole__field">
                        <div className="bob-devconsole__k">Cursor ops</div>
                        <div className="bob-devconsole__mini">devRefundOutstandingCursor / devRefundAndResetCursor / devResetRefundCursor</div>
                      </div>
                    </div>

                    <div className="bob-devconsole__btns">
                      <button type="button" className="max-btn" onClick={() => runDevTx("devRefundOutstandingCursor", () => presale.devRefundOutstandingCursor(safeInt(refundMaxSteps, 120)))} disabled={ownerBtnDisabled}>
                        Cursor refund step
                      </button>

                      <button
                        type="button"
                        className="max-btn"
                        onClick={() => runDevTx("devRefundAndResetCursor", () => presale.devRefundAndResetCursor(safeInt(refundMaxSteps, 120)))}
                        disabled={ownerBtnDisabled}
                        title="Runs cursor refunds and auto-resets only when fully complete."
                      >
                        Refund + Auto-Reset
                      </button>

                      <button type="button" className="max-btn" onClick={() => runDevTx("devResetRefundCursor", () => presale.devResetRefundCursor())} disabled={ownerBtnDisabled}>
                        Reset cursor to 0
                      </button>

                      <button type="button" className="max-btn" onClick={() => runDevTx("devResetState", () => presale.devResetState())} disabled={ownerBtnDisabled} title="Safe reset: only works when there are no outstanding contributions.">
                        Reset state (safe)
                      </button>
                    </div>

                    {/* Legacy batch refund buttons */}
                    <div className="bob-devconsole__btnrow" style={{ marginTop: "8px" }}>
                      <button type="button" className="max-btn" onClick={() => runDevTx("refundBatch:0..25", () => presale.refundBatch(0, 25))} disabled={ownerBtnDisabled}>
                        Batch Refund (0..25)
                      </button>
                      <button type="button" className="max-btn" onClick={() => runDevTx("refundBatch:25..50", () => presale.refundBatch(25, 25))} disabled={ownerBtnDisabled}>
                        Batch Refund (25..50)
                      </button>
                    </div>

                    {/* NEW v2.8.9 escape hatch */}
                    <div className="bob-devconsole__field" style={{ marginTop: "10px" }}>
                      <div className="bob-devconsole__k">Force refund stuck contract buyer → treasury (FAIL/CANCELED only)</div>
                      <input value={forceRefundBuyer} onChange={(e) => setForceRefundBuyer(e.target.value)} placeholder="0x... contract buyer address" />
                      <div className="bob-devconsole__mini">
                        Calls <span className="bob-devconsole__v--mono">devForceRefundToTreasury(buyer)</span>. Only works for contract addresses.
                      </div>
                      <div className="bob-devconsole__btnrow" style={{ marginTop: "6px" }}>
                        <button
                          type="button"
                          className="max-btn"
                          onClick={() => runDevTx("devForceRefundToTreasury", () => presale.devForceRefundToTreasury(forceRefundBuyer))}
                          disabled={ownerBtnDisabled || !String(forceRefundBuyer || "").trim()}
                          title="Use only when a contract buyer cannot receive refunds"
                        >
                          Force refund → treasury
                        </button>
                        <button type="button" className="max-btn" onClick={() => setForceRefundBuyer("")} disabled={ownerBtnDisabled}>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bob-devconsole__group bob-devconsole__group--wide">
                    <div className="bob-devconsole__label">Dev / Owner Settings</div>

                    <div className="bob-devconsole__form">
                      <div className="bob-devconsole__rowline">
                        <label className="bob-devconsole__switch">
                          <input type="checkbox" checked={devBypass} onChange={(e) => setDevBypass(e.target.checked)} disabled={ownerBtnDisabled} />
                          <span>Allowlist bypass (devMode)</span>
                        </label>
                        <button type="button" className="max-btn" onClick={() => runDevTx("devSetAllowlistBypass", () => presale.devSetAllowlistBypass(devBypass))} disabled={ownerBtnDisabled}>
                          Apply
                        </button>
                      </div>

                      <div className="bob-devconsole__hint">
                        Current: devMode={presale.devMode ? "ON" : "OFF"} | bypass={presale.devAllowlistBypass ? "ON" : "OFF"} | walletCapBps={presale.walletCapBps} | presaleAllocationBps={presale.presaleAllocationBps}
                      </div>

                      {/* Token address setter */}
                      <div className="bob-devconsole__field" style={{ marginTop: "10px" }}>
                        <div className="bob-devconsole__k">Token address (BOB token contract)</div>
                        <input value={setTokenAddr} onChange={(e) => setSetTokenAddr(e.target.value)} placeholder="0x... token address" />
                        <div className="bob-devconsole__mini">
                          On-chain token:{" "}
                          <span className="bob-devconsole__v--mono">{tokenAddrFromHook ? String(tokenAddrFromHook) : "(unset)"}</span>
                        </div>
                        <div className="bob-devconsole__btnrow" style={{ marginTop: "6px" }}>
                          <button
                            type="button"
                            className="max-btn"
                            onClick={() => setTokenAddressOnChain(setTokenAddr)}
                            disabled={ownerBtnDisabled || !isHexAddress(setTokenAddr)}
                            title={!isHexAddress(setTokenAddr) ? "Enter a valid 0x address first" : "Sets token address in the presale contract"}
                          >
                            Set token address
                          </button>
                          <button type="button" className="max-btn" onClick={() => setSetTokenAddr(tokenAddrFromHook || "")} disabled={ownerBtnDisabled}>
                            Copy from chain
                          </button>
                          <button type="button" className="max-btn" onClick={() => setSetTokenAddr("")} disabled={ownerBtnDisabled}>
                            Clear
                          </button>
                        </div>
                      </div>

                      {/* ✅ NEW: Claims enabled toggle */}
                      <div className="bob-devconsole__field" style={{ marginTop: "10px" }}>
                        <div className="bob-devconsole__k">Claims (enable/disable token claiming)</div>
                        <div className="bob-devconsole__mini">
                          On-chain claims: <span className="bob-devconsole__v--mono">{presale.claimsEnabled ? "ENABLED" : "DISABLED"}</span>
                        </div>
                        <div className="bob-devconsole__rowline" style={{ marginTop: "6px" }}>
                          <label className="bob-devconsole__switch">
                            <input 
                              type="checkbox" 
                              checked={claimsEnabledLocal} 
                              onChange={(e) => setClaimsEnabledLocal(e.target.checked)} 
                              disabled={ownerBtnDisabled} 
                            />
                            <span>Claims enabled</span>
                          </label>
                          <button 
                            type="button" 
                            className="max-btn" 
                            onClick={() => runDevTx("setClaimsEnabled", () => presale.setClaimsEnabled(claimsEnabledLocal))} 
                            disabled={ownerBtnDisabled}
                            title="Enable or disable token claiming in the presale contract"
                          >
                            Apply claims
                          </button>
                        </div>
                      </div>

                      <div className="bob-devconsole__btnrow" style={{ marginTop: "8px" }}>
                        <button type="button" className="max-btn" onClick={loadCurrentIntoInputs} disabled={ownerBtnDisabled} title="Copies on-chain values into the inputs">
                          Load current values
                        </button>

                        <button type="button" className="max-btn" onClick={applyAll} disabled={ownerBtnDisabled || applyAllBusy} title="Applies bypass + caps + rate + softcap + token + claims (+ times if valid)">
                          {applyAllBusy ? "Applying..." : "Apply ALL"}
                        </button>

                        {applyAllStatus ? <span className="bob-devconsole__mini">{applyAllStatus}</span> : null}
                      </div>

                      <div className="bob-devconsole__split">
                        <div className="bob-devconsole__field">
                          <div className="bob-devconsole__k">Presale allocation bps</div>
                          <input value={capPresaleBps} onChange={(e) => setCapPresaleBps(e.target.value)} placeholder="3000" />
                          <div className="bob-devconsole__mini">Example: 3000 = 30%</div>
                        </div>
                        <div className="bob-devconsole__field">
                          <div className="bob-devconsole__k">Wallet cap bps</div>
                          <input value={capWalletBps} onChange={(e) => setCapWalletBps(e.target.value)} placeholder="6" />
                          <div className="bob-devconsole__mini">Set to 10000 to let 1 wallet buy everything (dev).</div>
                        </div>
                      </div>

                      <div className="bob-devconsole__btnrow">
                        <button type="button" className="max-btn" onClick={() => runDevTx("setCapBps", () => presale.setCapBps(capPresaleBps, capWalletBps))} disabled={ownerBtnDisabled}>
                          Apply caps
                        </button>

                        <button type="button" className="max-btn" onClick={() => runDevTx("setCapBps:100%", () => presale.setCapBps(10000, 10000))} disabled={ownerBtnDisabled} title="Dev: let 1 wallet buy everything">
                          Dev: caps to 100%
                        </button>
                      </div>

                      <div className="bob-devconsole__split">
                        <div className="bob-devconsole__field">
                          <div className="bob-devconsole__k">tokensPerMon (BOB per 1 MON)</div>
                          <input value={setTpm} onChange={(e) => setSetTpm(e.target.value)} placeholder="1000" />
                          <button type="button" className="max-btn" onClick={() => runDevTx("setTokensPerMon", () => presale.setTokensPerMon(setTpm))} disabled={ownerBtnDisabled}>
                            Set rate
                          </button>
                        </div>

                        <div className="bob-devconsole__field">
                          <div className="bob-devconsole__k">Softcap (MON)</div>
                          <input value={setSoftcapMon} onChange={(e) => setSetSoftcapMon(e.target.value)} placeholder="10" />
                          <button type="button" className="max-btn" onClick={() => runDevTx("setSoftCapMon", () => presale.setSoftCapMon(setSoftcapMon))} disabled={ownerBtnDisabled}>
                            Set softcap
                          </button>
                        </div>
                      </div>

                      <div className="bob-devconsole__split">
                        <div className="bob-devconsole__field">
                          <div className="bob-devconsole__k">Start (unix seconds)</div>
                          <input value={setStartSec} onChange={(e) => setSetStartSec(e.target.value)} placeholder="e.g. 1730000000" />
                        </div>
                        <div className="bob-devconsole__field">
                          <div className="bob-devconsole__k">End (unix seconds)</div>
                          <input value={setEndSec} onChange={(e) => setSetEndSec(e.target.value)} placeholder="e.g. 1730003600" />
                        </div>
                      </div>

                      <div className="bob-devconsole__btnrow">
                        <button type="button" className="max-btn" onClick={() => runDevTx("setTimes", () => presale.setTimes(setStartSec, setEndSec))} disabled={ownerBtnDisabled}>
                          Set times
                        </button>
                        <button
                          type="button"
                          className="max-btn"
                          onClick={() => {
                            const now = Math.floor(Date.now() / 1000);
                            setSetStartSec(String(now + 5));
                            setSetEndSec(String(now + 600));
                          }}
                          disabled={ownerBtnDisabled}
                        >
                          Fill times (now+5, +10m)
                        </button>
                      </div>

                      <div className="bob-devconsole__field" style={{ marginTop: "8px" }}>
                        <div className="bob-devconsole__k">Private allowlist auth (deadline + signature)</div>

                        <div className="bob-devconsole__mini" style={{ marginBottom: "8px" }}>
                          {authHelperText}
                          <br />
                          Signer: <span className="bob-devconsole__v--mono">{presale.signer ? String(presale.signer) : "unknown"}</span>
                          <br />
                          Nonce: <span className="bob-devconsole__v--mono">{presale.userNonce ? String(presale.userNonce) : "0"}</span>
                          <br />
                          Hook auth:{" "}
                          <span className="bob-devconsole__v--mono">
                            {presale.authStatus?.state || "idle"}
                            {presale.authStatus?.msg ? " - " + presale.authStatus.msg : ""}
                          </span>
                        </div>

                        <div className="bob-devconsole__split">
                          <div className="bob-devconsole__field">
                            <div className="bob-devconsole__k">Local deadline (unix seconds)</div>
                            <input value={authDeadlineLocal} onChange={(e) => setAuthDeadlineLocal(e.target.value)} placeholder="e.g. 1730003600" disabled={!canEditAuth} />
                            <div className="bob-devconsole__mini">Stored only. Hook auto-fetches real auth when live.</div>
                          </div>

                          <div className="bob-devconsole__field">
                            <div className="bob-devconsole__k">Local signature (0x...)</div>
                            <input value={authSigLocal} onChange={(e) => setAuthSigLocal(e.target.value)} placeholder="0x..." disabled={!canEditAuth} />
                            <div className="bob-devconsole__mini">Stored only. Hook uses backend response for buys.</div>
                          </div>
                        </div>

                        <div className="bob-devconsole__btnrow">
                          <button type="button" className="max-btn" onClick={() => runDevTx("fetchAuth", () => presale.fetchAuth("manual"))} disabled={ownerBtnDisabled} title="Force an auth refresh from backend">
                            Fetch auth now
                          </button>

                          <button
                            type="button"
                            className="max-btn"
                            onClick={() => {
                              const now = Math.floor(Date.now() / 1000);
                              setAuthDeadlineLocal(String(now + 3600));
                            }}
                            disabled={ownerBtnDisabled}
                            title="Sets a local deadline 1 hour from now (does not generate a signature)"
                          >
                            Set local deadline (now+1h)
                          </button>

                          <button
                            type="button"
                            className="max-btn"
                            onClick={() => {
                              setAuthDeadlineLocal("");
                              setAuthSigLocal("");
                            }}
                            disabled={ownerBtnDisabled}
                          >
                            Clear local auth
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bob-devconsole__group bob-devconsole__group--wide">
                    <div className="bob-devconsole__label">Export</div>
                    <div className="bob-devconsole__hint" style={{ marginBottom: "8px" }}>
                      Download buyers list from the contract (address + MON contributed + tokens bought).
                    </div>

                    <div className="bob-devconsole__btnrow">
                      <button type="button" className="max-btn" onClick={() => exportBuyersToFile("json")} disabled={ownerBtnDisabled} title="Download JSON">
                        {exportBusy ? "Exporting..." : "Export buyers (JSON)"}
                      </button>
                      <button type="button" className="max-btn" onClick={() => exportBuyersToFile("csv")} disabled={ownerBtnDisabled} title="Download CSV">
                        {exportBusy ? "Exporting..." : "Export buyers (CSV)"}
                      </button>
                    </div>
                  </div>

                  <div className="bob-devconsole__group bob-devconsole__group--wide">
                    <div className="bob-devconsole__label">Status</div>
                    <div className="bob-devconsole__kv">
                      <div className="bob-devconsole__row">
                        <span className="bob-devconsole__k">Contract</span>
                        <span className="bob-devconsole__v">{activePresaleAddress ? activePresaleAddress.slice(0, 6) + "..." + activePresaleAddress.slice(-4) : "missing"}</span>
                      </div>

                      <div className="bob-devconsole__row">
                        <span className="bob-devconsole__k">Owner</span>
                        <span className="bob-devconsole__v bob-devconsole__v--mono">{ownerFromHook ? String(ownerFromHook) : "(owner not exposed by hook)"}</span>
                      </div>

                      <div className="bob-devconsole__row">
                        <span className="bob-devconsole__k">Token</span>
                        <span className="bob-devconsole__v bob-devconsole__v--mono">{tokenAddrFromHook ? String(tokenAddrFromHook) : "(unset)"}</span>
                      </div>

                      <div className="bob-devconsole__row">
                        <span className="bob-devconsole__k">Claims</span>
                        <span className="bob-devconsole__v bob-devconsole__v--mono">{presale.claimsEnabled ? "ENABLED" : "DISABLED"}</span>
                      </div>

                      <div className="bob-devconsole__row">
                        <span className="bob-devconsole__k">Signer</span>
                        <span className="bob-devconsole__v bob-devconsole__v--mono">{presale.signer ? String(presale.signer) : "unknown"}</span>
                      </div>

                      <div className="bob-devconsole__row">
                        <span className="bob-devconsole__k">Outstanding</span>
                        <span className="bob-devconsole__v bob-devconsole__v--mono">{outstandingMonDisplay} MON</span>
                      </div>

                      <div className="bob-devconsole__row">
                        <span className="bob-devconsole__k">Forced → treasury</span>
                        <span className="bob-devconsole__v bob-devconsole__v--mono">{forcedToTreasuryMonDisplay} MON</span>
                      </div>

                      <div className="bob-devconsole__row">
                        <span className="bob-devconsole__k">Refund cursor</span>
                        <span className="bob-devconsole__v bob-devconsole__v--mono">{refundCursorDisplay}</span>
                      </div>

                      <div className="bob-devconsole__row">
                        <span className="bob-devconsole__k">Tx</span>
                        <span className="bob-devconsole__v bob-devconsole__v--mono">{presale.txHash ? String(presale.txHash) : "none"}</span>
                      </div>

                      <div className="bob-devconsole__hint">
                        Debug: run <code>__presaleDebug()</code> in the console.
                      </div>
                    </div>
                  </div>

                  <div className="bob-devconsole__group bob-devconsole__group--wide">
                    <div className="bob-devconsole__label">Network</div>
                    <div className="bob-devconsole__hint">
                      expectedChainId={String(expectedChainId)} | walletChainId={String(walletChainId)} | wrongNetwork={isWrongNetwork ? "true" : "false"}
                    </div>
                  </div>

                  <div className="bob-devconsole__group bob-devconsole__group--wide">
                    <div className="bob-devconsole__label">Console (captured)</div>

                    <div className="bob-devconsole__btnrow" style={{ marginBottom: "8px" }}>
                      <button
                        type="button"
                        className="max-btn"
                        onClick={() => {
                          setDevConsoleLines([]);
                          devConsoleLinesRef.current = [];
                        }}
                        disabled={!devLogsOn || ownerBtnDisabled}
                        title="Clears the captured console output"
                      >
                        Clear
                      </button>

                      <button
                        type="button"
                        className="max-btn"
                        onClick={() => {
                          try {
                            const text = devConsoleLinesRef.current
                              .map((l) => `[${new Date(l.t).toLocaleTimeString()}] ${l.level.toUpperCase()}: ${l.msg}`)
                              .join("\n");
                            navigator.clipboard.writeText(text);
                            alert("Copied captured logs to clipboard.");
                          } catch {
                            alert("Could not copy logs (clipboard blocked).");
                          }
                        }}
                        disabled={!devLogsOn || !devConsoleLines.length || ownerBtnDisabled}
                        title="Copy captured logs to clipboard"
                      >
                        Copy
                      </button>
                    </div>

                    <div className={"bob-devconsolebox " + (!devLogsOn ? "bob-devconsolebox--off" : "")}>
                      {!devLogsOn ? (
                        <div className="bob-devconsolebox__empty">Turn Logs ON to capture console output.</div>
                      ) : devConsoleLines.length ? (
                        devConsoleLines.map((l, i) => (
                          <div key={i} className={"line line--" + l.level}>
                            <span className="t">{new Date(l.t).toLocaleTimeString()}</span>
                            <span className="lvl">{l.level}</span>
                            <span className="msg">{l.msg}</span>
                          </div>
                        ))
                      ) : (
                        <div className="bob-devconsolebox__empty">No logs captured yet.</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={"bob-devbackdrop " + (devOpen ? "bob-devbackdrop--open" : "")} onClick={() => setDevOpen(false)} />
        </>
      ) : null}

      <div id="presale-page">
        <div className="presale-bg" aria-hidden="true" style={{ "--presale-bg-image": 'url("/presale/bobchest.webp")' }} />

        <header className={"presale-hero " + (mounted ? "in" : "")}>
          <div className="presale-container hero-grid">
            <div className="hero-copy-block">
              <div className="hero-top-row">
                <span className="hero-kicker-pill">Phase 1 - MON Presale</span>
                <span className={"status-pill status-" + statusKey}>{statusLabel}</span>
              </div>

              <h1 className="hero-title">
                <strong>$BOB</strong> <span className="presale-outline">Presale</span>
</h1>
          <p className="hero-subtitle">{heroSubtitle}</p>

          <div className="monad-chain-pill">
            <span className="made-on-text">Made on</span>
            <img src="/monad/monad.png" alt="Monad chain logo" className="monad-logo" />
          </div>
        </div>

        <div className="hero-image-block">
          <div className="hero-image-card">
            <div className="hero-image-inner">
              <img src="/presale/bobchest.webp" alt="$BOB Presale" />
            </div>
          </div>
        </div>
      </div>
    </header>

    <main className="presale-main">
      <div className="presale-container presale-layout">
        {/* LEFT */}
        <section className="presale-panel stats-panel" style={{ "--panel-bg-image": "url('/presale/bobmoon.png')" }}>
          <div className="panel-header">
            <div>
              <h2>BOB Presale Overview</h2>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat">
              <span className="label">Status</span>
              <span className="value" title={statusBoxLabel}>
                {statusBoxLabel}
              </span>
            </div>

            {/* single-line price */}
            <div className="stat">
              <span className="label">Presale Price</span>
              <span className="value" title={`1 MON ~ ${Math.round(bobPerMonValueUi).toLocaleString()} BOB`}>
                1 MON ~ {bobPerMonDisplayUi} BOB
              </span>
            </div>

            {/* compact Raised */}
            <div className="stat">
              <span className="label">Raised</span>
              <span className="value" title={`Raised: ${raisedMonUi.toLocaleString()} / ${targetMonTotalUi.toLocaleString()} MON`}>
                {raisedMonShortUi} MON
              </span>
            </div>

            <div className="stat">
              <span className="label">Participants</span>
              <span className="value" title={`${participantsUi.toLocaleString()} / ${participantsStatusCapUi.toLocaleString()} spots`}>
                {participantsUi.toLocaleString()} / {participantsStatusCapUi.toLocaleString()}
              </span>
            </div>

          </div>

          <div className="progress-block">
            <div className="progress-header">
              <span>Presale Progress</span>
              <span className="progress-percent">{progressPctUi.toFixed(1)}%</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: Math.min(progressPctUi, 100) + "%" }} />
            </div>

            {/* ✅ REVERTED to original style here (as requested) */}
            <div className="progress-meta">
              <span title={`Raised: ${raisedMonUi.toLocaleString()} / ${targetMonTotalUi.toLocaleString()} MON`}>
                Raised: {raisedMonUi.toLocaleString()} / {targetMonTotalUi.toLocaleString()} <span className="stat-unit">MON</span>
              </span>
              <span title={`Supply left: ${progressSupplyLeftBobUi.toLocaleString()} / ${progressSupplyCapBobUi.toLocaleString()} $BOB`}>
                Supply left: {progressSupplyLeftBobUi.toLocaleString()} / {progressSupplyCapBobUi.toLocaleString()} $BOB
              </span>
            </div>
          </div>

          <div className="allocation-block">
            <div className="allocation-header">
              <h3>Token Allocation</h3>
            </div>

            <div className="allocation-content">
              <div className="allocation-pie-wrap" aria-hidden="true">
                <div className="allocation-pie" style={{ "--allocation-presale-pct": presaleAllocationPctUi }} />
                <div className="allocation-supply">
                  <span className="supply-label">Total</span>
                  <span className="supply-value">1B</span>
                </div>
              </div>

              <ul className="allocation-legend">
                <li>
                  <span className="dot dot-presale" />
                  <span className="legend-text">
                    <span className="legend-label">Presale</span>
                    <span className="legend-meta">
                      {presaleAllocationPctUi}% - {presaleAllocationBobUi.toLocaleString()}
                    </span>
                  </span>
                </li>
                <li>
                  <span className="dot dot-liquidity" />
                  <span className="legend-text">
                    <span className="legend-label">Initial Liquidity</span>
                    <span className="legend-meta">
                      {liquidityAllocationPctUi}% - {liquidityAllocationBobUi.toLocaleString()}
                    </span>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* RIGHT */}
        <section className="presale-panel form-panel" style={{ "--panel-bg-image": "url('/presale/bobhot.webp')" }}>
          <div className="panel-header">
            <div>
              <h2>Participate in BOB Presale</h2>
              <p className="tagline">{rightTagline}</p>
            </div>

            {canShowOwnerTools ? (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className={"dev-ui-toggle " + (devPreviewUserMode ? "dev-ui-toggle--on" : "")}
                  onClick={() => setDevPreviewUserMode((v) => !v)}
                  title="Hide dev info inside the presale UI"
                >
                  {devPreviewUserMode ? "User View" : "Dev View"}
                </button>

                <button
                  type="button"
                  className={"dev-ui-toggle " + (devPreviewPersonaActive ? "dev-ui-toggle--on" : "")}
                  onClick={() =>
                    setDevUserPreviewState((prev) => {
                      const i = DEV_USER_PREVIEW_STATES.indexOf(prev);
                      const next = DEV_USER_PREVIEW_STATES[(i + 1) % DEV_USER_PREVIEW_STATES.length];
                      setDevMockParticipated(false);
                      return next;
                    })
                  }
                  title="Cycle user preview state: Ineligible -> Eligible (Not bought) -> Eligible (Bought) -> Off"
                >
                  {devUserPreviewLabel}
                </button>
              </div>
            ) : null}
          </div>

          <div className={"buy-area " + (shouldDisableUi ? "buy-area--disabled" : "")}>
            <div className="field-group">
              <label>
                Fixed Allocation (<span className="bob-font bob-font--small">MON</span>)
              </label>
              <div className="helper">Each approved wallet participates with a fixed {allocationMonDisplay} MON allocation.</div>
            </div>

            <div className="field-group summary-row">
              <div className="summary-item">
                <span className="label">You will receive</span>
                <span className="value" title={`${bobPerWalletDisplayUi} BOB`}>
                  {bobPerWalletDisplayUi} <span className="bob-font bob-font--small">BOB</span>
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Fixed allocation</span>
                <span className="value" title={`${allocationMonDisplay} MON`}>
                  {allocationMonDisplay} <span className="bob-font bob-font--small">MON</span>
                </span>
              </div>
            </div>

            <div className="cta-block">
              <div className={"wallet-status " + (wallet.isConnected ? "wallet-status--connected" : "wallet-status--disconnected")}>
                {wallet.isConnected ? "Wallet connected: " + (wallet.shortAddress || shortAddress) : "Wallet not connected"}
              </div>

              {/* ✅ Stable 1-line notice bar (always rendered; fixed height = no layout jump) */}
              <div
                className={"notice-bar notice-bar--" + (notice.level || "info")}
                aria-live="polite"
                style={{
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  marginTop: "10px",
                  padding: "0 10px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  opacity: authStateLine ? 1 : 0,
                  transition: "opacity 160ms ease",
                }}
                title={authStateLine || ""}
              >
                {authStateLine}
              </div>

              {showDevInfoInUi ? (
                <div className="helper helper-tight">
                  Allowlist mode: Private (signature)
                  <span className="helper-subline">
                    devMode: {presale.devMode ? "ON" : "OFF"} | bypass: {presale.devAllowlistBypass ? "ON" : "OFF"} | token:{" "}
                    <span className="bob-devconsole__v--mono">{tokenAddrFromHook ? shortAddr(tokenAddrFromHook) : "(unset)"}</span> | claims:{" "}
                    <span className="bob-devconsole__v--mono">{presale.claimsEnabled ? "ENABLED" : "DISABLED"}</span> | refundCursor:{" "}
                    <span className="bob-devconsole__v--mono">{refundCursorDisplay}</span>
                    {" | outstanding(MON): "}
                    <span className="bob-devconsole__v--mono">{outstandingMonDisplay}</span>
                    {autoLiveActive ? (
                      <>
                        {" "}
                        | <span className="bob-devconsole__v--mono">auto LIVE in {autoLiveCountdown || "..."}</span>
                      </>
                    ) : null}
                  </span>
                </div>
              ) : null}

              {/* ✅ STATIC CTA WHEN NOT LIVE (keeps the text but makes it non-interactive) */}
              {statusKey === "live" ? (
                <button type="button" className="buy-btn" onClick={handlePrimaryCta} disabled={buyButtonDisabled}>
                  {effectiveBuyBtnText}
                </button>
              ) : (
                <div
                  className="buy-btn"
                  role="status"
                  aria-label={effectiveBuyBtnText}
                  style={{
                    userSelect: "none",
                    cursor: "default",
                    opacity: 0.92,
                    pointerEvents: "none",
                  }}
                  title={effectiveBuyBtnText}
                >
                  {effectiveBuyBtnText}
                </div>
              )}

            </div>
          </div>

          <div className="info-panel" style={{ marginTop: "14px" }}>
            <h3>Presale Details</h3>
            <ul>
              <li>
                <span className="info-label">Contribution</span>
                <span className="info-value">
                  {allocationMonDisplay} <span className="bob-font bob-font--small">MON</span> per wallet
                </span>
              </li>
              <li>
                <span className="info-label">BOB Allocation</span>
                <span className="info-value">
                  {bobPerWalletDisplayUi} <span className="bob-font bob-font--small">BOB</span> per wallet
                </span>
              </li>
              <li>
                <span className="info-label">Presale Spots</span>
                <span className="info-value">{maxParticipantsUi.toLocaleString()} total</span>
              </li>
              <li>
                <span className="info-label">Access Model</span>
                <span className="info-value">Whitelist + first come first serve</span>
              </li>
              <li>
                <span className="info-label">Spots Filled</span>
                <span className="info-value">
                  {participantsUi.toLocaleString()} / {maxParticipantsUi.toLocaleString()}
                </span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </main>

    <Footer />
  </div>
</>
);
}
